import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, UserRole, ROLE_NAMES, AdminTabType, hasAccess, canAccessAdminPanel, ROLE_PERMISSIONS } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, onValue, remove, set, off } from 'firebase/database';
import { db, realtimeDb } from '../config/firebase';
import '../components/ChatWidget.css';
import ShinyText from '../components/ShinyText';
import {
  Users,
  Shield,
  UserCheck,
  UserX,
  Crown,
  RefreshCw,
  Search,
  Filter,
  Eye,
  CheckCircle,
  Clock,
  Check,
  X,
  Plus,
  Star,
  MessageCircle,
  Ban,
  Timer,
  Trash2,
  User,
  Send,
  Minus,
  Store,
  Edit,
  Save,
  Headphones,
  Gamepad2,
  TrendingUp,
  Megaphone,
  AlertTriangle,
  Gift,
  Image,
  ArrowUp,
  ArrowDown,
  Flame,
  Trophy,
  ChevronDown,
  Layers,
  Dices,
  Download,
  Database,
  FileJson,
  CheckCircle2
} from 'lucide-react';
import AdminLiveSupport from '../components/AdminLiveSupport';
import SEO from '../components/SEO';
import RichTextEditor from '../components/RichTextEditor';

const AdminPanel: React.FC = () => {
  const { currentUser, userData, isAdmin, getAllUsers, updateUserRole, toggleUserStatus } = useAuth();
  const { onlineUsers } = useChat();
  const { tickets } = useLiveSupport();
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | UserRole>('all');
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<AdminTabType>('users');

  // Game Management state'leri
  const [games, setGames] = useState<any[]>([]);
  const [isGameEditModalOpen, setIsGameEditModalOpen] = useState(false);
  const [editingGame, setEditingGame] = useState<any>(null);
  const [gameSearchTerm, setGameSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [isApplicationDetailOpen, setIsApplicationDetailOpen] = useState(false);
  // Bonus Kodları state'leri
  const [bonusCodes, setBonusCodes] = useState<any[]>([]);
  const [editingBonusCode, setEditingBonusCode] = useState<any>(null);
  const [isBonusCodeEditModalOpen, setIsBonusCodeEditModalOpen] = useState(false);
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [selectedSponsor, setSelectedSponsor] = useState<any>(null);
  const [isSponsorDetailOpen, setIsSponsorDetailOpen] = useState(false);
  
  // Chat moderasyon state'leri
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [chatFilter, setChatFilter] = useState<'all' | 'recent' | 'reported'>('all');
  const [adminMessage, setAdminMessage] = useState('');
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('adminChatFontSize');
    return saved ? parseInt(saved) : 14;
  });
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Ban ve timeout yönetimi state'leri
  const [bannedUsers, setBannedUsers] = useState<any[]>([]);
  const [timeoutUsers, setTimeoutUsers] = useState<any[]>([]);
  const [showBanTimeoutPanel, setShowBanTimeoutPanel] = useState(false);
  const [banTimeoutSearchTerm, setBanTimeoutSearchTerm] = useState('');
  
  // Market yönetimi state'leri
  const [marketProducts, setMarketProducts] = useState<any[]>([]);
  const [isProductEditModalOpen, setIsProductEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [marketFilter, setMarketFilter] = useState<'all' | 'vehicles' | 'clothing' | 'weapons' | 'properties' | 'accessories' | 'games'>('all');

  // Banner yönetimi state'leri
  const [banners, setBanners] = useState<any[]>([]);
  const [isBannerEditModalOpen, setIsBannerEditModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<any>(null);

  // Promosyon yönetimi state'leri
  const [promotions, setPromotions] = useState<any[]>([]);
  const [isPromoEditModalOpen, setIsPromoEditModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<any>(null);

  // Turnuva yönetimi state'leri
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [isTournamentEditModalOpen, setIsTournamentEditModalOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState<any>(null);

  // Sayfa yönetimi state'leri
  const [pagesDropdownOpen, setPagesDropdownOpen] = useState(false);
  const [slotsDropdownOpen, setSlotsDropdownOpen] = useState(false);
  const [casinoDropdownOpen, setCasinoDropdownOpen] = useState(false);
  const [selectedPageType, setSelectedPageType] = useState<string>('');
  const [pageGames, setPageGames] = useState<any[]>([]);
  const [isPageGameEditModalOpen, setIsPageGameEditModalOpen] = useState(false);
  const [editingPageGame, setEditingPageGame] = useState<any>(null);

  // Lobiden oyun seçme state'leri
  const [lobbyGames, setLobbyGames] = useState<any[]>([]);
  const [isLobbySelectModalOpen, setIsLobbySelectModalOpen] = useState(false);
  const [lobbySearchTerm, setLobbySearchTerm] = useState('');
  const [selectedLobbyGames, setSelectedLobbyGames] = useState<Set<string>>(new Set());

  // Anasayfa popüler oyunlar için lobiden seçme state'leri
  const [isHomeLobbySelectModalOpen, setIsHomeLobbySelectModalOpen] = useState(false);
  const [homeLobbySearchTerm, setHomeLobbySearchTerm] = useState('');
  const [selectedHomeLobbyGames, setSelectedHomeLobbyGames] = useState<Set<string>>(new Set());

  // Backup state'leri
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupProgress, setBackupProgress] = useState<string>('');
  const [backupComplete, setBackupComplete] = useState(false);

  // Backup fonksiyonu - Tüm Firestore verilerini indir
  const downloadBackup = async () => {
    setBackupLoading(true);
    setBackupComplete(false);
    setBackupProgress('Yedekleme başlatılıyor...');

    const collectionsToBackup = [
      'users',
      'banners',
      'populerOyunlar',
      'pageGames_slots_lobby',
      'pageGames_slots_populer',
      'pageGames_slots_yeni',
      'pageGames_slots_freespin',
      'pageGames_casino_lobby',
      'pageGames_casino_masa',
      'pageGames_casino_rulet',
      'pageGames_casino_blackjack',
      'promosyonlar',
      'turnuvalar',
      'bonusKodlari',
      'sponsors',
      'marketProducts',
      'messages',
      'applications'
    ];

    try {
      const backup: { metadata: any; data: Record<string, any[]> } = {
        metadata: {
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.email || 'unknown',
          collections: collectionsToBackup,
          totalCollections: collectionsToBackup.length
        },
        data: {}
      };

      let totalDocs = 0;

      for (let i = 0; i < collectionsToBackup.length; i++) {
        const collectionName = collectionsToBackup[i];
        setBackupProgress(`${collectionName} koleksiyonu yükleniyor... (${i + 1}/${collectionsToBackup.length})`);

        try {
          const snapshot = await getDocs(collection(db, collectionName));
          const docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          backup.data[collectionName] = docs;
          totalDocs += docs.length;
        } catch (error) {
          console.warn(`${collectionName} koleksiyonu yüklenemedi:`, error);
          backup.data[collectionName] = [];
        }
      }

      backup.metadata.totalDocuments = totalDocs;
      setBackupProgress('JSON dosyası oluşturuluyor...');

      // JSON dosyasını oluştur ve indir
      const jsonString = JSON.stringify(backup, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
      const fileName = `firestore-backup-${timestamp}.json`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setBackupProgress(`Yedekleme tamamlandı! ${totalDocs} döküman, ${collectionsToBackup.length} koleksiyon`);
      setBackupComplete(true);
    } catch (error) {
      console.error('Yedekleme hatası:', error);
      setBackupProgress('Yedekleme sırasında bir hata oluştu!');
    } finally {
      setBackupLoading(false);
    }
  };

  // Game yükleme fonksiyonu
  const loadGames = useCallback(async () => {
    try {
      const gamesQuery = query(collection(db, 'populerOyunlar'), orderBy('order', 'asc'));
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesData = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGames(gamesData);
    } catch (error) {
      console.error('Oyunlar yüklenirken hata:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersData = await getAllUsers();
      
      // Online kullanıcıları kontrol et
      const usersWithOnlineStatus = usersData.map(user => ({
        ...user,
        isOnline: onlineUsers.some(onlineUser => onlineUser.userId === user.uid)
      }));
      
      setUsers(usersWithOnlineStatus);
      setError('');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [getAllUsers, onlineUsers]);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const messagesQuery = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesData = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMessages(messagesData);
      setError('');
    } catch (err) {
      console.error('Mesajlar yüklenirken hata oluştu:', err);
      setError('Mesajlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const markMessageAsRead = async (messageId: string) => {
    try {
      await updateDoc(doc(db, 'messages', messageId), {
        status: 'read',
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status: 'read' } : msg
      ));
    } catch (err) {
      console.error('Mesaj durumu güncellenirken hata oluştu:', err);
    }
  };

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      const applicationsQuery = query(collection(db, 'forms'), orderBy('createdAt', 'desc'));
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applicationsData = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setApplications(applicationsData);
      setError('');
    } catch (err) {
      console.error('Başvurular yüklenirken hata oluştu:', err);
      setError('Başvurular yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSponsors = useCallback(async () => {
    try {
      const sponsorsQuery = query(collection(db, 'sponsors'), orderBy('createdAt', 'desc'));
      const sponsorsSnapshot = await getDocs(sponsorsQuery);
      const sponsorsData = sponsorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSponsors(sponsorsData);
    } catch (error) {
      console.error('Sponsorlar yüklenirken hata:', error);
    }
  }, []);

  const loadMarketProducts = useCallback(async () => {
    try {
      const productsQuery = query(collection(db, 'marketProducts'), orderBy('createdAt', 'desc'));
      const productsSnapshot = await getDocs(productsQuery);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMarketProducts(productsData);
    } catch (error) {
      console.error('Market ürünleri yüklenirken hata:', error);
    }
  }, []);

  // Banner yükleme fonksiyonu
  const loadBanners = useCallback(async () => {
    try {
      const bannersQuery = query(collection(db, 'banners'), orderBy('order', 'asc'));
      const bannersSnapshot = await getDocs(bannersQuery);
      const bannersData = bannersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBanners(bannersData);
    } catch (error) {
      console.error('Bannerlar yüklenirken hata:', error);
    }
  }, []);

  // Market ürünleri CRUD fonksiyonları
  const addMarketProduct = async (productData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'marketProducts'), {
        ...productData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      await loadMarketProducts();
      return docRef.id;
    } catch (error) {
      console.error('Ürün eklenirken hata:', error);
      throw error;
    }
  };

  const updateMarketProduct = async (productId: string, productData: any) => {
    try {
      const productRef = doc(db, 'marketProducts', productId);
      await updateDoc(productRef, {
        ...productData,
        updatedAt: new Date()
      });
      await loadMarketProducts();
    } catch (error) {
      console.error('Ürün güncellenirken hata:', error);
      throw error;
    }
  };

  const deleteMarketProduct = async (productId: string) => {
    try {
      await deleteDoc(doc(db, 'marketProducts', productId));
      await loadMarketProducts();
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      throw error;
    }
  };

  // Banner CRUD fonksiyonları
  const addBanner = async (bannerData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'banners'), {
        ...bannerData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      await loadBanners();
      return docRef.id;
    } catch (error) {
      console.error('Banner eklenirken hata:', error);
      throw error;
    }
  };

  const updateBanner = async (bannerId: string, bannerData: any) => {
    try {
      await updateDoc(doc(db, 'banners', bannerId), {
        ...bannerData,
        updatedAt: new Date()
      });
      await loadBanners();
    } catch (error) {
      console.error('Banner güncellenirken hata:', error);
      throw error;
    }
  };

  const deleteBanner = async (bannerId: string) => {
    try {
      await deleteDoc(doc(db, 'banners', bannerId));
      await loadBanners();
    } catch (error) {
      console.error('Banner silinirken hata:', error);
      throw error;
    }
  };

  // Promosyon CRUD fonksiyonları
  const loadPromotions = useCallback(async () => {
    try {
      const promoQuery = query(collection(db, 'promotions'), orderBy('order', 'asc'));
      const promoSnapshot = await getDocs(promoQuery);
      const promoData = promoSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPromotions(promoData);
    } catch (error) {
      console.error('Promosyonlar yüklenirken hata:', error);
    }
  }, []);

  const addPromotion = async (promoData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'promotions'), {
        ...promoData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await loadPromotions();
      return docRef.id;
    } catch (error) {
      console.error('Promosyon eklenirken hata:', error);
      throw error;
    }
  };

  const updatePromotion = async (promoId: string, promoData: any) => {
    try {
      await updateDoc(doc(db, 'promotions', promoId), {
        ...promoData,
        updatedAt: serverTimestamp()
      });
      await loadPromotions();
    } catch (error) {
      console.error('Promosyon güncellenirken hata:', error);
      throw error;
    }
  };

  const deletePromotion = async (promoId: string) => {
    try {
      await deleteDoc(doc(db, 'promotions', promoId));
      await loadPromotions();
    } catch (error) {
      console.error('Promosyon silinirken hata:', error);
      throw error;
    }
  };

  // Turnuva CRUD fonksiyonları
  const loadTournaments = useCallback(async () => {
    try {
      const tournamentQuery = query(collection(db, 'tournaments'), orderBy('order', 'asc'));
      const tournamentSnapshot = await getDocs(tournamentQuery);
      const tournamentData = tournamentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTournaments(tournamentData);
    } catch (error) {
      console.error('Turnuvalar yüklenirken hata:', error);
    }
  }, []);

  const addTournament = async (tournamentData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'tournaments'), {
        ...tournamentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await loadTournaments();
      return docRef.id;
    } catch (error) {
      console.error('Turnuva eklenirken hata:', error);
      throw error;
    }
  };

  const updateTournament = async (tournamentId: string, tournamentData: any) => {
    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        ...tournamentData,
        updatedAt: serverTimestamp()
      });
      await loadTournaments();
    } catch (error) {
      console.error('Turnuva güncellenirken hata:', error);
      throw error;
    }
  };

  const deleteTournament = async (tournamentId: string) => {
    try {
      await deleteDoc(doc(db, 'tournaments', tournamentId));
      await loadTournaments();
    } catch (error) {
      console.error('Turnuva silinirken hata:', error);
      throw error;
    }
  };

  // Sayfa oyunlarını yükle
  const loadPageGames = useCallback(async (pageType: string) => {
    if (!pageType) return;
    try {
      const collectionName = `pageGames_${pageType}`;
      const gamesQuery = query(collection(db, collectionName), orderBy('order', 'asc'));
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesData = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPageGames(gamesData);
    } catch (error) {
      console.error('Sayfa oyunları yüklenirken hata:', error);
      setPageGames([]);
    }
  }, []);

  const addPageGame = async (gameData: any) => {
    if (!selectedPageType) return;
    try {
      const collectionName = `pageGames_${selectedPageType}`;
      const docRef = await addDoc(collection(db, collectionName), {
        ...gameData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await loadPageGames(selectedPageType);
      return docRef.id;
    } catch (error) {
      console.error('Oyun eklenirken hata:', error);
      throw error;
    }
  };

  const updatePageGame = async (gameId: string, gameData: any) => {
    if (!selectedPageType) return;
    try {
      const collectionName = `pageGames_${selectedPageType}`;
      await updateDoc(doc(db, collectionName, gameId), {
        ...gameData,
        updatedAt: serverTimestamp()
      });
      await loadPageGames(selectedPageType);
    } catch (error) {
      console.error('Oyun güncellenirken hata:', error);
      throw error;
    }
  };

  const deletePageGame = async (gameId: string) => {
    if (!selectedPageType) return;
    try {
      const collectionName = `pageGames_${selectedPageType}`;
      await deleteDoc(doc(db, collectionName, gameId));
      await loadPageGames(selectedPageType);
    } catch (error) {
      console.error('Oyun silinirken hata:', error);
      throw error;
    }
  };

  // Lobby oyunlarını yükle (slots veya casino)
  const loadLobbyGames = useCallback(async (type: 'slots' | 'casino') => {
    try {
      const collectionName = type === 'slots' ? 'pageGames_slots_lobby' : 'pageGames_casino_lobby';
      const gamesQuery = query(collection(db, collectionName), orderBy('order', 'asc'));
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesData = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLobbyGames(gamesData);
    } catch (error) {
      console.error('Lobby oyunları yüklenirken hata:', error);
      setLobbyGames([]);
    }
  }, []);

  // Lobiden seçilen oyunları hedef sayfaya ekle
  const addGamesFromLobby = async (selectedIds: Set<string>) => {
    if (!selectedPageType || selectedIds.size === 0) return;

    try {
      const collectionName = `pageGames_${selectedPageType}`;
      const currentMaxOrder = pageGames.length > 0
        ? Math.max(...pageGames.map(g => g.order || 0))
        : 0;

      let orderCounter = currentMaxOrder;
      const gamesToAdd: any[] = [];

      selectedIds.forEach((gameId) => {
        const game = lobbyGames.find(g => g.id === gameId);
        if (game) {
          gamesToAdd.push(game);
        }
      });

      for (let i = 0; i < gamesToAdd.length; i++) {
        const game = gamesToAdd[i];
        orderCounter++;
        await addDoc(collection(db, collectionName), {
          name: game.name,
          provider: game.provider,
          imageUrl: game.imageUrl,
          gameUrl: game.gameUrl,
          order: orderCounter,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      await loadPageGames(selectedPageType);
      setIsLobbySelectModalOpen(false);
      setSelectedLobbyGames(new Set());
      setLobbySearchTerm('');
    } catch (error) {
      console.error('Oyunlar eklenirken hata:', error);
    }
  };

  // Anasayfa popüler oyunlarına lobby'den oyun ekle
  const addGamesToHomePopular = async (selectedIds: Set<string>) => {
    if (selectedIds.size === 0) return;

    try {
      const currentMaxOrder = games.length > 0
        ? Math.max(...games.map(g => g.order || 0))
        : 0;

      let orderCounter = currentMaxOrder;
      const gamesToAdd: any[] = [];

      selectedIds.forEach((gameId) => {
        const game = lobbyGames.find(g => g.id === gameId);
        if (game) {
          gamesToAdd.push(game);
        }
      });

      for (let i = 0; i < gamesToAdd.length; i++) {
        const game = gamesToAdd[i];
        orderCounter++;
        await addDoc(collection(db, 'populerOyunlar'), {
          name: game.name,
          provider: game.provider,
          imageUrl: game.imageUrl,
          gameUrl: game.gameUrl,
          rating: 4.5,
          playCount: 0,
          commentCount: 0,
          trend: 'stable',
          order: orderCounter,
          isPopular: true,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      await loadGames();
      setIsHomeLobbySelectModalOpen(false);
      setSelectedHomeLobbyGames(new Set());
      setHomeLobbySearchTerm('');
    } catch (error) {
      console.error('Oyunlar eklenirken hata:', error);
    }
  };

  // Chat mesajlarını yükle
  const loadChatMessages = useCallback(async () => {
    try {
      const messagesRef = ref(realtimeDb, 'messages');
      onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const messagesList = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          })).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          setChatMessages(messagesList);
        } else {
          setChatMessages([]);
        }
      });
    } catch (error) {
      console.error('Chat mesajları yüklenirken hata:', error);
    }
  }, []);

  // Chat moderasyon aksiyonları
  const deleteMessage = async (messageId: string) => {
    try {
      await remove(ref(realtimeDb, `messages/${messageId}`));
      // State güncellemesine gerek yok, listener otomatik güncelleyecek
    } catch (error) {
      console.error('Mesaj silinirken hata:', error);
    }
  };

  const banUser = async (userId: string, reason: string) => {
    try {
      // Firestore'da kullanıcıyı banla
      await updateDoc(doc(db, 'users', userId), {
        isBanned: true,
        banReason: reason,
        bannedAt: new Date(),
        bannedBy: currentUser?.uid
      });
      
      // Realtime Database'de ban bilgisini kaydet
      try {
        const banData = {
          reason,
          bannedAt: Date.now(),
          bannedBy: currentUser?.uid,
          bannedByEmail: currentUser?.email
        };
        
        await set(ref(realtimeDb, `bans/${userId}`), banData);
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile Firestore işlemi başarılı oldu
        // Hata loglanmaz - production güvenliği için
      }
      
      // Banlanan kullanıcı listesini yenile
      await loadBannedUsers();
      
    } catch (error: any) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      setError('Kullanıcı banlanırken bir hata oluştu');
    }
  };

  const timeoutUser = async (userId: string, duration: number, reason: string) => {
    try {
      const timeoutUntil = Date.now() + (duration * 60 * 1000); // dakika cinsinden
      
      // Realtime Database'de timeout bilgisini kaydet
      try {
        await set(ref(realtimeDb, `timeouts/${userId}`), {
          reason,
          timeoutUntil,
          timedOutAt: Date.now(),
          timedOutBy: currentUser?.uid,
          timedOutByEmail: currentUser?.email
        });
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile işlem devam edebilir
        // Hata loglanmaz - production güvenliği için
      }
      
      // Timeout kullanıcı listesini yenile
      await loadTimeoutUsers();
      
    } catch (error: any) {
      setError('Kullanıcı timeout edilirken bir hata oluştu');
    }
  };

  // Banlanan kullanıcıları yükle
  const loadBannedUsers = useCallback(async () => {
    try {
      // Firestore'dan banlanan kullanıcıları al
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const bannedUsersList = usersSnapshot.docs
        .map(doc => {
          const userData = doc.data();
          const processedUser = {
            id: doc.id,
            ...userData,
            // Kullanıcı adı için farklı alanları kontrol et
            displayName: userData.displayName || userData.name || userData.username || userData.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
            email: userData.email || 'Email bulunamadı'
          };
          
          return processedUser;
        })
        .filter((user: any) => user.isBanned === true);
      
      setBannedUsers(bannedUsersList);
    } catch (error) {
      console.error('Banlanan kullanıcılar yüklenirken hata:', error);
    }
  }, []);

  // Timeout edilen kullanıcıları yükle
  const loadTimeoutUsers = useCallback(async () => {
    try {
      // Realtime Database'den timeout bilgilerini al
      const timeoutRef = ref(realtimeDb, 'timeouts');
      onValue(timeoutRef, async (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const timeoutUsersList = await Promise.all(
            Object.keys(data).map(async (userId) => {
              const timeoutData = data[userId];
              const now = Date.now();
              const timeoutUntil = timeoutData.timeoutUntil;
              
              // Sadece aktif timeout'ları göster
              if (timeoutUntil && now < timeoutUntil) {
                // Firestore'dan kullanıcı bilgilerini al
                try {
                  const userDoc = await getDoc(doc(db, 'users', userId));
                  const userData = userDoc.exists() ? userDoc.data() : {};
                  
                  const processedUser = {
                    id: userId,
                    ...timeoutData,
                    // Kullanıcı adı için farklı alanları kontrol et
                    displayName: userData.displayName || userData.name || userData.username || timeoutData.displayName || timeoutData.username || userData.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
                    email: userData.email || timeoutData.email || 'Email bulunamadı',
                    remainingTime: Math.ceil((timeoutUntil - now) / 60000) // dakika cinsinden
                  };
                  
                  
                  return processedUser;
                } catch (error) {
                  console.error('Kullanıcı bilgileri alınırken hata:', error);
                  return {
                    id: userId,
                    ...timeoutData,
                    displayName: timeoutData.displayName || timeoutData.username || 'Bilinmeyen Kullanıcı',
                    email: timeoutData.email || 'Email bulunamadı',
                    remainingTime: Math.ceil((timeoutUntil - now) / 60000)
                  };
                }
              }
              return null;
            })
          );
          
          setTimeoutUsers(timeoutUsersList.filter(user => user !== null));
        } else {
          setTimeoutUsers([]);
        }
      });
    } catch (error) {
      console.error('Timeout kullanıcıları yüklenirken hata:', error);
    }
  }, []);

  // Kullanıcının banını aç
  const unbanUser = async (userId: string) => {
    try {
      // Firestore'da kullanıcının banını kaldır
      await updateDoc(doc(db, 'users', userId), {
        isBanned: false,
        banReason: null,
        bannedAt: null,
        bannedBy: null,
        unbannedAt: new Date(),
        unbannedBy: currentUser?.uid
      });
      
      // Realtime Database'den ban bilgisini sil
      try {
        await remove(ref(realtimeDb, `bans/${userId}`));
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile Firestore işlemi başarılı oldu
        // Hata loglanmaz - production güvenliği için
      }
      
      // Banlanan kullanıcı listesini yenile
      await loadBannedUsers();
    } catch (error: any) {
      setError('Kullanıcının banı açılırken bir hata oluştu');
    }
  };

  // Kullanıcının timeout'unu kaldır
  const removeTimeout = async (userId: string) => {
    try {
      // Realtime Database'den timeout bilgisini sil
      try {
        await remove(ref(realtimeDb, `timeouts/${userId}`));
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile işlem devam edebilir
        // Hata loglanmaz - production güvenliği için
      }
      
      // Timeout kullanıcı listesini yenile
      await loadTimeoutUsers();
    } catch (error: any) {
      setError('Kullanıcının timeout\'u kaldırılırken bir hata oluştu');
    }
  };

  // Admin mesaj gönder
  const sendAdminMessage = async () => {
    if (!adminMessage.trim() || !currentUser) return;

    try {
      const messageData = {
        id: Date.now().toString(),
        text: adminMessage.trim(),
        userId: currentUser.uid,
        username: currentUser.displayName || 'Admin',
        timestamp: Date.now(),
        isAdmin: true
      };

      // Realtime Database'e mesajı ekle
      await set(ref(realtimeDb, `messages/${messageData.id}`), messageData);
      
      // Input'u temizle
      setAdminMessage('');
      
    } catch (error: any) {
      setError('Mesaj gönderilirken bir hata oluştu');
    }
  };

  // Font boyutu değiştir
  const changeFontSize = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('adminChatFontSize', newSize.toString());
  };

  // Chat container'ı en alta scroll et
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Mesajlar değiştiğinde en alta scroll et
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Chat listener cleanup
  useEffect(() => {
    const messagesRef = ref(realtimeDb, 'messages');
    
    return () => {
      off(messagesRef);
    };
  }, []);

  const updateApplicationStatus = async (applicationId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      await updateDoc(doc(db, 'forms', applicationId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setApplications(prev => prev.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
    } catch (err) {
      console.error('Başvuru durumu güncellenirken hata oluştu:', err);
    }
  };

  const updateSponsorStatus = async (sponsorId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      await updateDoc(doc(db, 'sponsors', sponsorId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setSponsors(prev => prev.map(sponsor => 
        sponsor.id === sponsorId ? { ...sponsor, status: newStatus } : sponsor
      ));
    } catch (err) {
      console.error('Sponsor durumu güncellenirken hata oluştu:', err);
    }
  };

  const handleViewApplicationDetail = (application: any) => {
    setSelectedApplication(application);
    setIsApplicationDetailOpen(true);
  };

  const handleViewSponsorDetail = (sponsor: any) => {
    setSelectedSponsor(sponsor);
    setIsSponsorDetailOpen(true);
  };

  // Bonus kodlarını yükleme fonksiyonu
  const loadBonusCodes = useCallback(async () => {
    try {
      const bonusCodesQuery = query(collection(db, 'bonusCodes'), orderBy('createdAt', 'desc'));
      const bonusCodesSnapshot = await getDocs(bonusCodesQuery);
      const bonusCodesData = bonusCodesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBonusCodes(bonusCodesData);
    } catch (err) {
      console.error('Bonus kodları yüklenirken hata oluştu:', err);
    }
  }, []);

  // Bonus kodu ekleme fonksiyonu
  const addBonusCode = async (codeData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'bonusCodes'), {
        ...codeData,
        usageCount: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Local state'i güncelle
      setBonusCodes((prev: any[]) => [{ id: docRef.id, ...codeData, usageCount: 0 }, ...prev]);

      return docRef.id;
    } catch (error) {
      console.error('Bonus kodu eklenirken hata:', error);
      throw error;
    }
  };

  // Bonus kodu silme fonksiyonu
  const deleteBonusCode = async (codeId: string) => {
    try {
      await deleteDoc(doc(db, 'bonusCodes', codeId));

      // Local state'i güncelle
      setBonusCodes((prev: any[]) => prev.filter((code: any) => code.id !== codeId));
    } catch (error) {
      console.error('Bonus kodu silinirken hata:', error);
      throw error;
    }
  };

  // Bonus kodu güncelleme fonksiyonu
  const updateBonusCode = async (codeId: string, codeData: any) => {
    try {
      await updateDoc(doc(db, 'bonusCodes', codeId), {
        ...codeData,
        updatedAt: new Date()
      });

      // Local state'i güncelle
      setBonusCodes((prev: any[]) => prev.map((code: any) =>
        code.id === codeId ? { ...code, ...codeData } : code
      ));
    } catch (error) {
      console.error('Bonus kodu güncellenirken hata:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/');
      return;
    }

    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'messages') {
      loadMessages();
    } else if (activeTab === 'applications') {
      loadApplications();
    } else if (activeTab === 'sponsors') {
      loadSponsors();
    } else if (activeTab === 'market') {
      loadMarketProducts();
    } else if (activeTab === 'chat') {
      loadChatMessages();
      loadBannedUsers();
      loadTimeoutUsers();
    } else if (activeTab === 'banners') {
      loadBanners();
    } else if (activeTab === 'games') {
      loadGames();
    } else if (activeTab === 'promo') {
      loadPromotions();
    } else if (activeTab === 'tournaments') {
      loadTournaments();
    } else if (activeTab === 'bonusCodes') {
      loadBonusCodes();
    } else if (activeTab === 'pages' && selectedPageType) {
      loadPageGames(selectedPageType);
    }
  }, [currentUser, isAdmin, navigate, loadUsers, loadMessages, loadApplications, loadSponsors, loadMarketProducts, loadChatMessages, loadBannedUsers, loadTimeoutUsers, loadBanners, loadGames, loadPromotions, loadTournaments, activeTab, loadBonusCodes, loadPageGames, selectedPageType]);

  const handleRoleChange = async (uid: string, newRole: UserRole) => {
    try {
      await updateUserRole(uid, newRole);
      await loadUsers(); // Refresh the list
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleStatusToggle = async (uid: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      
      // Loading state'i başlat
      setUpdatingUsers(prev => new Set(prev).add(uid));
      
      await toggleUserStatus(uid, newStatus);
      
      // Optimistic update - UI'yi hemen güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === uid 
            ? { ...user, isActive: newStatus }
            : user
        )
      );
      
      // Sonra veritabanından güncel veriyi çek
      await loadUsers();
      
    } catch (error: any) {
      setError(error.message || 'Durum güncellenirken bir hata oluştu');
    } finally {
      // Loading state'i bitir
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(uid);
        return newSet;
      });
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'Bilinmiyor';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Kullanıcıları online/offline durumlarına göre kategorize et
  const usersWithStatus = users.map(user => {
    const isOnline = onlineUsers.some(onlineUser => onlineUser.userId === user.uid);
    return {
      ...user,
      isOnline
    };
  });

  const filteredUsers = usersWithStatus.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // İstatistikler
  const totalUsers = users.length;
  const activeUsers = users.filter(user => user.isActive).length;
  const adminUsers = users.filter(user => user.role === 'admin').length;
  const onlineUsersCount = onlineUsers.length;
  const offlineUsersCount = totalUsers - onlineUsersCount;


  // Kullanıcının rolüne göre ilk erişilebilir tab'ı belirle
  useEffect(() => {
    if (userData?.role && userData.role !== 'user') {
      const availableTabs = ROLE_PERMISSIONS[userData.role] || [];
      if (availableTabs.length > 0 && !hasAccess(userData.role, activeTab)) {
        setActiveTab(availableTabs[0]);
      }
    }
  }, [userData?.role, activeTab]);

  // Admin paneline erişim kontrolü
  if (!currentUser || !userData || !canAccessAdminPanel(userData.role)) {
    return null;
  }

  const userRole = userData.role;

  return (
    <>
      <SEO 
        title="Admin Panel - Aedon Community" 
        description="Aedon Community admin paneli - Kullanıcı yönetimi"
        keywords="admin, panel, kullanıcı yönetimi, aedon community"
      />
      
      <div className="min-h-screen bg-dark-900 pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-gaming font-bold text-white mb-2">
                  Admin Panel
                </h1>
                <p className="text-gray-400 text-sm sm:text-base">
                  Kullanıcı yönetimi ve sistem kontrolü
                </p>
              </div>
              
              <button
                onClick={loadUsers}
                disabled={loading}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors duration-300"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Yenile</span>
              </button>
            </div>
          </div>

          {/* Tab Navigation - Role Based */}
          <div className="mb-8 relative" style={{ zIndex: 99999 }}>
            <div className="bg-dark-800/80 backdrop-blur-xl border border-dark-700/50 rounded-2xl p-2 shadow-xl overflow-visible">
              <div className="flex flex-wrap gap-2 overflow-visible">
                {/* Users - Admin, Risk */}
                {hasAccess(userRole, 'users') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('users')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'users'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Users className={`w-4 h-4 ${activeTab === 'users' ? 'text-white' : 'text-blue-400'}`} />
                    <span className="font-medium">Kullanıcılar</span>
                    {activeTab === 'users' && (
                      <motion.div
                        layoutId="activeTabIndicator"
                        className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl -z-10"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </motion.button>
                )}

                {/* Chat Management - Admin, Live Support */}
                {hasAccess(userRole, 'chat') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('chat')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'chat'
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <MessageCircle className={`w-4 h-4 ${activeTab === 'chat' ? 'text-white' : 'text-indigo-400'}`} />
                    <span className="font-medium">Chat</span>
                    {chatMessages.length > 0 && (
                      <span className="ml-1 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                        {chatMessages.length}
                      </span>
                    )}
                  </motion.button>
                )}

                {/* Live Support - Admin, Live Support */}
                {hasAccess(userRole, 'support') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('support')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'support'
                        ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Headphones className={`w-4 h-4 ${activeTab === 'support' ? 'text-white' : 'text-emerald-400'}`} />
                    <span className="font-medium">Canlı Destek</span>
                    {tickets.filter(t => t.status === 'waiting').length > 0 && (
                      <span className="ml-1 bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                        {tickets.filter(t => t.status === 'waiting').length}
                      </span>
                    )}
                  </motion.button>
                )}

                {/* Game Management - Admin, Game Management */}
                {hasAccess(userRole, 'games') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('games')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'games'
                        ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg shadow-orange-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Gamepad2 className={`w-4 h-4 ${activeTab === 'games' ? 'text-white' : 'text-orange-400'}`} />
                    <span className="font-medium">Oyunlar</span>
                  </motion.button>
                )}

                {/* Promo Management - Admin, Promo Management */}
                {/* Bonus Kodları - Admin only */}
                {hasAccess(userRole, 'bonusCodes') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('bonusCodes')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'bonusCodes'
                        ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Gift className={`w-4 h-4 ${activeTab === 'bonusCodes' ? 'text-white' : 'text-emerald-400'}`} />
                    <span className="font-medium">Bonus Kodları</span>
                  </motion.button>
                )}

                {hasAccess(userRole, 'promo') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('promo')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'promo'
                        ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg shadow-pink-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Gift className={`w-4 h-4 ${activeTab === 'promo' ? 'text-white' : 'text-pink-400'}`} />
                    <span className="font-medium">Promosyon</span>
                  </motion.button>
                )}

                {/* Tournament Management - Admin, Promo Management */}
                {hasAccess(userRole, 'tournaments') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('tournaments')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'tournaments'
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-lg shadow-amber-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Trophy className={`w-4 h-4 ${activeTab === 'tournaments' ? 'text-white' : 'text-amber-400'}`} />
                    <span className="font-medium">Turnuvalar</span>
                    {tournaments.length > 0 && (
                      <span className="ml-1 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                        {tournaments.length}
                      </span>
                    )}
                  </motion.button>
                )}

                {/* Banner Management - Admin, Promo Management */}
                {hasAccess(userRole, 'banners') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('banners')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'banners'
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Image className={`w-4 h-4 ${activeTab === 'banners' ? 'text-white' : 'text-violet-400'}`} />
                    <span className="font-medium">Banner</span>
                    {banners.length > 0 && (
                      <span className="ml-1 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                        {banners.length}
                      </span>
                    )}
                  </motion.button>
                )}

                {/* Market Management - Admin, Promo Management */}
                {hasAccess(userRole, 'market') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('market')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'market'
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg shadow-teal-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Store className={`w-4 h-4 ${activeTab === 'market' ? 'text-white' : 'text-teal-400'}`} />
                    <span className="font-medium">Market</span>
                    {marketProducts.length > 0 && (
                      <span className="ml-1 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                        {marketProducts.length}
                      </span>
                    )}
                  </motion.button>
                )}

                {/* Marketing - Admin, Marketing */}
                {hasAccess(userRole, 'marketing') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('marketing')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'marketing'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Megaphone className={`w-4 h-4 ${activeTab === 'marketing' ? 'text-white' : 'text-cyan-400'}`} />
                    <span className="font-medium">Marketing</span>
                  </motion.button>
                )}

                {/* Risk Management - Admin, Risk */}
                {hasAccess(userRole, 'risk') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('risk')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'risk'
                        ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <AlertTriangle className={`w-4 h-4 ${activeTab === 'risk' ? 'text-white' : 'text-red-400'}`} />
                    <span className="font-medium">Risk</span>
                  </motion.button>
                )}

                {/* Sponsors - Admin only */}
                {hasAccess(userRole, 'sponsors') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('sponsors')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'sponsors'
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow-lg shadow-yellow-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Star className={`w-4 h-4 ${activeTab === 'sponsors' ? 'text-white' : 'text-yellow-400'}`} />
                    <span className="font-medium">Sponsorlar</span>
                    {sponsors.filter(sponsor => sponsor.status === 'pending').length > 0 && (
                      <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                        {sponsors.filter(sponsor => sponsor.status === 'pending').length}
                      </span>
                    )}
                  </motion.button>
                )}

                {/* Sayfalar (Pages) Management - Admin only */}
                {hasAccess(userRole, 'pages') && (
                  <div className="relative">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setPagesDropdownOpen(!pagesDropdownOpen);
                        setActiveTab('pages');
                      }}
                      className={`relative flex items-center justify-between w-full px-4 py-2.5 rounded-xl transition-all duration-300 ${
                        activeTab === 'pages'
                          ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-500/25'
                          : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Layers className={`w-4 h-4 ${activeTab === 'pages' ? 'text-white' : 'text-indigo-400'}`} />
                        <span className="font-medium">Sayfalar</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${pagesDropdownOpen ? 'rotate-180' : ''}`} />
                    </motion.button>

                    {/* Pages Dropdown */}
                    <AnimatePresence>
                      {pagesDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute left-0 right-0 top-full mt-1 bg-dark-800 border border-dark-600 rounded-xl p-2 space-y-1 shadow-xl"
                          style={{ zIndex: 99999 }}
                        >
                          {/* Slotlar Dropdown */}
                          <div>
                            <button
                              onClick={() => setSlotsDropdownOpen(!slotsDropdownOpen)}
                              className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-dark-700/50 rounded-lg transition-colors"
                            >
                              <div className="flex items-center space-x-2">
                                <Dices className="w-4 h-4 text-purple-400" />
                                <span>Slotlar</span>
                              </div>
                              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${slotsDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                              {slotsDropdownOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="ml-4 mt-1 space-y-1"
                                >
                                  {[
                                    { key: 'slots_lobby', label: 'Lobby' },
                                    { key: 'slots_popular', label: 'Popüler Oyunlar' },
                                    { key: 'slots_new', label: 'Yeni Çıkanlar' },
                                    { key: 'slots_freespins', label: 'FreeSpinler' }
                                  ].map((item) => (
                                    <button
                                      key={item.key}
                                      onClick={() => {
                                        setSelectedPageType(item.key);
                                        loadPageGames(item.key);
                                      }}
                                      className={`w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                        selectedPageType === item.key
                                          ? 'bg-purple-500/20 text-purple-300'
                                          : 'text-gray-500 hover:text-gray-300 hover:bg-dark-700/30'
                                      }`}
                                    >
                                      {item.label}
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          {/* Canlı Casino Dropdown */}
                          <div>
                            <button
                              onClick={() => setCasinoDropdownOpen(!casinoDropdownOpen)}
                              className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-dark-700/50 rounded-lg transition-colors"
                            >
                              <div className="flex items-center space-x-2">
                                <Gamepad2 className="w-4 h-4 text-green-400" />
                                <span>Canlı Casino</span>
                              </div>
                              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${casinoDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                              {casinoDropdownOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="ml-4 mt-1 space-y-1"
                                >
                                  {[
                                    { key: 'casino_lobby', label: 'Canlı Lobi' },
                                    { key: 'casino_table', label: 'Masa Oyunları' },
                                    { key: 'casino_roulette', label: 'Rulet' },
                                    { key: 'casino_blackjack', label: 'Blackjack' }
                                  ].map((item) => (
                                    <button
                                      key={item.key}
                                      onClick={() => {
                                        setSelectedPageType(item.key);
                                        loadPageGames(item.key);
                                      }}
                                      className={`w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                        selectedPageType === item.key
                                          ? 'bg-green-500/20 text-green-300'
                                          : 'text-gray-500 hover:text-gray-300 hover:bg-dark-700/30'
                                      }`}
                                    >
                                      {item.label}
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Backup - Admin only */}
                {hasAccess(userRole, 'backup') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab('backup')}
                    className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                      activeTab === 'backup'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                    }`}
                  >
                    <Database className={`w-4 h-4 ${activeTab === 'backup' ? 'text-white' : 'text-cyan-400'}`} />
                    <span className="font-medium">Yedekleme</span>
                  </motion.button>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Toplam Kullanıcı</p>
                  <p className="text-2xl font-bold text-white">{totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-primary-500" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Çevrimiçi</p>
                  <p className="text-2xl font-bold text-green-400">{onlineUsersCount}</p>
                </div>
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Çevrimdışı</p>
                  <p className="text-2xl font-bold text-gray-400">{offlineUsersCount}</p>
                </div>
                <div className="w-3 h-3 bg-gray-500 rounded-full" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Admin Sayısı</p>
                  <p className="text-2xl font-bold text-yellow-400">{adminUsers}</p>
                </div>
                <Crown className="w-8 h-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Aktif Kullanıcı</p>
                  <p className="text-2xl font-bold text-white">{activeUsers}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          {activeTab === 'users' && (
            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6 mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Kullanıcı adı veya e-posta ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value as 'all' | UserRole)}
                    className="px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">Tüm Roller</option>
                    {(Object.keys(ROLE_NAMES) as UserRole[]).map(role => (
                      <option key={role} value={role}>{ROLE_NAMES[role]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Kullanıcılar yükleniyor...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-dark-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        E-posta
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Online
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Son IP
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Cihaz / Tarayıcı
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Kayıt Tarihi
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-700/50">
                    {filteredUsers.map((user) => (
                      <tr key={user.uid} className="hover:bg-dark-700/30 transition-colors duration-200">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-400">
                                  {user.username.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">
                                {user.displayName || user.username}
                              </div>
                              <div className="text-sm text-gray-400">
                                @{user.username}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300">{user.email}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-yellow-500/20 text-yellow-400' :
                            user.role === 'live_support' ? 'bg-green-500/20 text-green-400' :
                            user.role === 'risk' ? 'bg-red-500/20 text-red-400' :
                            user.role === 'game_management' ? 'bg-purple-500/20 text-purple-400' :
                            user.role === 'promo_management' ? 'bg-pink-500/20 text-pink-400' :
                            user.role === 'marketing' ? 'bg-cyan-500/20 text-cyan-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {user.role === 'admin' ? <Crown className="w-3 h-3 mr-1" /> : <Shield className="w-3 h-3 mr-1" />}
                            {ROLE_NAMES[user.role as UserRole] || 'Kullanıcı'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleStatusToggle(user.uid, user.isActive)}
                            disabled={updatingUsers.has(user.uid)}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                              user.isActive 
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                            }`}
                            title={`${user.isActive ? 'Pasifleştirmek için tıklayın' : 'Aktifleştirmek için tıklayın'}`}
                          >
                            {updatingUsers.has(user.uid) ? (
                              <>
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                Güncelleniyor...
                              </>
                            ) : user.isActive ? (
                              <>
                                <UserCheck className="w-3 h-3 mr-1" />
                                Aktif
                              </>
                            ) : (
                              <>
                                <UserX className="w-3 h-3 mr-1" />
                                Pasif
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${user.isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                            <span className={`text-sm ${user.isOnline ? 'text-green-400' : 'text-gray-400'}`}>
                              {user.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300 font-mono">
                            {user.lastLoginIP || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {user.lastDevice ? (
                            <div className="text-xs">
                              <div className="text-gray-300">
                                {user.lastDevice.device} {user.lastDevice.isMobile && '📱'}
                              </div>
                              <div className="text-gray-500">
                                {user.lastDevice.browser} {user.lastDevice.browserVersion && `v${user.lastDevice.browserVersion}`}
                              </div>
                              <div className="text-gray-500">
                                {user.lastDevice.os} {user.lastDevice.osVersion}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <select
                              value={user.role}
                              onChange={(e) => handleRoleChange(user.uid, e.target.value as UserRole)}
                              className="px-2 py-1 bg-dark-700/50 border border-dark-600/50 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                            >
                              {(Object.keys(ROLE_NAMES) as UserRole[]).map(role => (
                                <option key={role} value={role}>{ROLE_NAMES[role]}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </motion.div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Mesajlar yükleniyor...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Gönderen
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          E-posta
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Konu
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-700/50">
                      {messages.map((message) => (
                        <tr key={message.id} className="hover:bg-dark-700/30 transition-colors duration-200">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {message.status === 'unread' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                  <span className="text-xs text-red-400 font-medium">Okunmamış</span>
                                </div>
                              ) : message.status === 'read' ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-400 font-medium">Okundu</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4 text-blue-500" />
                                  <span className="text-xs text-blue-400 font-medium">Yanıtlandı</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{message.name}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">{message.email}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-white max-w-xs truncate">{message.subject}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {message.createdAt ? new Date(message.createdAt.seconds * 1000).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => markMessageAsRead(message.id)}
                                disabled={message.status === 'read'}
                                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                              >
                                <Eye className="w-3 h-3" />
                                <span>Okundu İşaretle</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {messages.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                            Henüz mesaj bulunmuyor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Başvurular yükleniyor...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Kullanıcı
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Karakter Adı
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-700/50">
                      {applications.map((application) => (
                        <tr key={application.id} className="hover:bg-dark-700/30 transition-colors duration-200">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {application.status === 'pending' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <span className="text-xs text-yellow-400 font-medium">Beklemede</span>
                                </div>
                              ) : application.status === 'approved' ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-400 font-medium">Onaylandı</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <X className="w-4 h-4 text-red-500" />
                                  <span className="text-xs text-red-400 font-medium">Reddedildi</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{application.userEmail}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">{application.characterName}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {application.createdAt ? new Date(application.createdAt.seconds * 1000).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {application.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateApplicationStatus(application.id, 'approved')}
                                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                  >
                                    <Check className="w-3 h-3" />
                                    <span>Onayla</span>
                                  </button>
                                  <button
                                    onClick={() => updateApplicationStatus(application.id, 'rejected')}
                                    className="flex items-center space-x-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                    <span>Reddet</span>
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleViewApplicationDetail(application)}
                                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                              >
                                <Eye className="w-3 h-3" />
                                <span>Detay</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {applications.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                            Henüz başvuru bulunmuyor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* Sponsors Tab */}
          {activeTab === 'sponsors' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Sponsorlar yükleniyor...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Şirket
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Sponsorluk Türü
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Bütçe
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-700/50">
                      {sponsors.map((sponsor) => (
                        <tr key={sponsor.id} className="hover:bg-dark-700/30 transition-colors duration-200">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {sponsor.status === 'pending' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <span className="text-xs text-yellow-400 font-medium">Beklemede</span>
                                </div>
                              ) : sponsor.status === 'approved' ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-400 font-medium">Onaylandı</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <X className="w-4 h-4 text-red-500" />
                                  <span className="text-xs text-red-400 font-medium">Reddedildi</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-white">{sponsor.companyName}</div>
                              <div className="text-sm text-gray-400">{sponsor.contactPerson}</div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{sponsor.sponsorshipType}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{sponsor.budget}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {sponsor.createdAt?.toDate ? 
                                sponsor.createdAt.toDate().toLocaleDateString('tr-TR') : 
                                'Bilinmiyor'
                              }
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewSponsorDetail(sponsor)}
                                className="text-primary-400 hover:text-primary-300 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {sponsor.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateSponsorStatus(sponsor.id, 'approved')}
                                    className="text-green-400 hover:text-green-300 transition-colors"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => updateSponsorStatus(sponsor.id, 'rejected')}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {sponsors.length === 0 && (
                    <div className="text-center py-12">
                      <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-400 mb-2">Henüz sponsor başvurusu yok</h3>
                      <p className="text-gray-500">Sponsor başvuruları burada görünecek</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Market Management Tab */}
          {activeTab === 'market' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">Market Yönetimi</h3>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setEditingProduct({
                          name: '',
                          category: 'vehicles',
                          description: '',
                          price: 0,
                          currency: 'TL',
                          image: '',
                          features: [],
                          author: currentUser?.displayName || 'Admin',
                          version: '1.0.0',
                          rating: 5.0,
                          downloads: 0,
                          players: 0,
                          server: ''
                        });
                        setIsProductEditModalOpen(true);
                      }}
                      className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Yeni Ürün Ekle</span>
                    </button>
                  </div>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Ürün ara..."
                        value={marketSearchTerm}
                        onChange={(e) => setMarketSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={marketFilter}
                      onChange={(e) => setMarketFilter(e.target.value as any)}
                      className="px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">Tüm Kategoriler</option>
                      <option value="vehicles">Araçlar</option>
                      <option value="clothing">Kıyafetler</option>
                      <option value="weapons">Silahlar</option>
                      <option value="properties">Mülkler</option>
                      <option value="accessories">Aksesuarlar</option>
                      <option value="games">Oyunlar</option>
                    </select>
                  </div>
                </div>

                {/* Products List */}
                <div className="space-y-4">
                  {marketProducts
                    .filter(product => {
                      const matchesSearch = product.name.toLowerCase().includes(marketSearchTerm.toLowerCase()) ||
                                           product.description.toLowerCase().includes(marketSearchTerm.toLowerCase());
                      const matchesFilter = marketFilter === 'all' || product.category === marketFilter;
                      return matchesSearch && matchesFilter;
                    })
                    .map((product) => (
                    <div key={product.id} className="bg-dark-700/50 border border-dark-600 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div>
                            <h4 className="text-lg font-semibold text-white">{product.name}</h4>
                            <p className="text-gray-400 text-sm">{product.description}</p>
                            <div className="flex items-center space-x-4 mt-2">
                              <span className="text-green-400 font-bold">{product.price.toLocaleString()} {product.currency}</span>
                              <span className="text-gray-400 text-sm">Kategori: {product.category}</span>
                              <span className="text-gray-400 text-sm">Geliştirici: {product.author}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingProduct(product);
                              setIsProductEditModalOpen(true);
                            }}
                            className="p-2 text-blue-400 hover:text-blue-300 hover:bg-dark-600 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteMarketProduct(product.id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-dark-600 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {marketProducts.length === 0 && (
                    <div className="text-center py-12">
                      <Store className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-400 mb-2">
                        Henüz ürün bulunmuyor
                      </h3>
                      <p className="text-gray-500">
                        İlk ürünü eklemek için "Yeni Ürün Ekle" butonuna tıklayın
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Live Support Tab */}
          {activeTab === 'support' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <AdminLiveSupport />
            </motion.div>
          )}

          {/* Chat Management Tab */}
          {activeTab === 'chat' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">Chat Yönetimi</h3>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setShowBanTimeoutPanel(true)}
                      className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-gray-300"
                      title="Ban ve Timeout Yönetimi"
                    >
                      <Shield className="w-4 h-4" />
                      {(bannedUsers.length > 0 || timeoutUsers.length > 0) && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {bannedUsers.length + timeoutUsers.length}
                        </span>
                      )}
                    </button>
                    
                    {/* Font Boyutu Ayarlama */}
                    <div className="flex items-center space-x-2 bg-gray-700/50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-300">A-</span>
                      <button
                        onClick={() => changeFontSize(Math.max(10, fontSize - 2))}
                        className="text-gray-400 hover:text-white transition-colors"
                        title="Yazı boyutunu küçült"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-white min-w-[2rem] text-center">{fontSize}px</span>
                      <button
                        onClick={() => changeFontSize(Math.min(24, fontSize + 2))}
                        className="text-gray-400 hover:text-white transition-colors"
                        title="Yazı boyutunu büyüt"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-300">A+</span>
                    </div>
                    
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Mesaj ara..."
                        value={chatSearchTerm}
                        onChange={(e) => setChatSearchTerm(e.target.value)}
                        className="w-64 px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                    </div>
                    <select
                      value={chatFilter}
                      onChange={(e) => setChatFilter(e.target.value as 'all' | 'recent' | 'reported')}
                      className="px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">Tüm Mesajlar</option>
                      <option value="recent">Son Mesajlar</option>
                      <option value="reported">Raporlanan</option>
                    </select>
                  </div>
                </div>

                <div 
                  ref={chatContainerRef}
                  className="space-y-4 max-h-96 overflow-y-auto"
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {chatMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-400 mb-2">Henüz mesaj yok</h3>
                      <p className="text-gray-500">Chat mesajları burada görünecek</p>
                    </div>
                  ) : (
                    chatMessages
                      .filter(message => {
                        const matchesSearch = message.text?.toLowerCase().includes(chatSearchTerm.toLowerCase()) ||
                                            message.username?.toLowerCase().includes(chatSearchTerm.toLowerCase());
                        return matchesSearch;
                      })
                      .map((message) => {
                        const isAdminMessage = message.isAdmin || message.userId === currentUser?.uid;
                        return (
                          <div key={message.id} className={`${isAdminMessage ? 'ml-8' : 'mr-8'}`}>
                            <div className={`bg-dark-700/30 rounded-lg p-4 border border-dark-600/50 ${isAdminMessage ? 'bg-primary-600/20 border-primary-500/30' : ''}`}>
                              <div className={`flex ${isAdminMessage ? 'justify-end' : 'justify-between'} items-start mb-3`}>
                                {!isAdminMessage && (
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="text-white font-medium flex items-center space-x-2">
                                        {message.isAdmin ? (
                                          <ShinyText 
                                            text={message.username || 'Admin'} 
                                            speed={7} 
                                            variant="black-white"
                                            className="font-bold text-sm"
                                          />
                                        ) : (
                                          <ShinyText 
                                            text={message.username || 'Bilinmeyen Kullanıcı'} 
                                            speed={5} 
                                            className="font-medium text-sm"
                                          />
                                        )}
                                        {message.isAdmin && <Shield className="w-4 h-4 text-yellow-400" />}
                                      </h4>
                                      <p className="text-xs text-gray-400">
                                        {message.timestamp ? new Date(message.timestamp).toLocaleString('tr-TR') : 'Bilinmeyen Tarih'}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                
                                {isAdminMessage && (
                                  <div className="flex items-center space-x-3">
                                    <div>
                                      <h4 className="text-white font-medium flex items-center space-x-2">
                                        <ShinyText 
                                          text={message.username || 'Admin'} 
                                          speed={7} 
                                          variant="black-white"
                                          className="font-bold text-sm"
                                        />
                                        <Shield className="w-4 h-4 text-yellow-400" />
                                      </h4>
                                      <p className="text-xs text-gray-400">
                                        {message.timestamp ? new Date(message.timestamp).toLocaleString('tr-TR') : 'Bilinmeyen Tarih'}
                                      </p>
                                    </div>
                                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                                      <Shield className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                )}
                                
                                {!isAdminMessage && (
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => deleteMessage(message.id)}
                                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                      title="Mesajı Sil"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => banUser(message.userId, 'Chat moderasyonu')}
                                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                      title="Kullanıcıyı Banla"
                                    >
                                      <Ban className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => timeoutUser(message.userId, 10, 'Chat moderasyonu')}
                                      className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-colors"
                                      title="10 Dakika Timeout"
                                    >
                                      <Timer className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className={`bg-dark-600/30 rounded-lg p-3 ${isAdminMessage ? 'bg-primary-500/20' : ''}`}>
                                <p className="text-gray-300">{message.text || 'Mesaj içeriği yok'}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
                
                {/* Admin Mesaj Gönderme Alanı */}
                <div className="mt-6 border-t border-dark-600 pt-6">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                    <MessageCircle className="w-5 h-5 text-primary-400" />
                    <span>Admin Mesajı Gönder</span>
                  </h4>
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Mesajınızı yazın..."
                        value={adminMessage}
                        onChange={(e) => setAdminMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendAdminMessage()}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <button
                      onClick={sendAdminMessage}
                      disabled={!adminMessage.trim()}
                      className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Gönder</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Banner Management Tab */}
          {activeTab === 'banners' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white">Banner Yönetimi</h3>
                  <button
                    onClick={() => {
                      setEditingBanner({
                        title: '',
                        description: '',
                        imageUrl: '',
                        linkUrl: '',
                        isActive: true,
                        order: banners.length
                      });
                      setIsBannerEditModalOpen(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Banner</span>
                  </button>
                </div>

                {banners.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Henüz banner eklenmemiş</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {banners.map((banner, index) => (
                      <div
                        key={banner.id}
                        className="flex items-center space-x-4 p-4 bg-dark-700/50 rounded-lg border border-dark-600"
                      >
                        <div className="w-32 h-20 rounded-lg overflow-hidden bg-dark-600 flex-shrink-0">
                          {banner.imageUrl ? (
                            <img
                              src={banner.imageUrl}
                              alt={banner.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Star className="w-8 h-8 text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium truncate">{banner.title || 'Başlıksız'}</h4>
                          <p className="text-gray-400 text-sm truncate">{banner.description || 'Açıklama yok'}</p>
                          <div className="flex items-center space-x-3 mt-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              banner.isActive
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {banner.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                            <span className="text-gray-500 text-xs">Sıra: {banner.order}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingBanner(banner);
                              setIsBannerEditModalOpen(true);
                            }}
                            className="p-2 text-gray-400 hover:text-primary-400 transition-colors"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Bu banner\'ı silmek istediğinize emin misiniz?')) {
                                await deleteBanner(banner.id);
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Banner Edit Modal */}
          <AnimatePresence>
            {isBannerEditModalOpen && editingBanner && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setIsBannerEditModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-dark-800 rounded-xl border border-dark-700 w-full max-w-lg max-h-[90vh] overflow-y-auto"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-6">
                      {editingBanner.id ? 'Banner Düzenle' : 'Yeni Banner'}
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Başlık
                        </label>
                        <input
                          type="text"
                          value={editingBanner.title || ''}
                          onChange={(e) => setEditingBanner({ ...editingBanner, title: e.target.value })}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Banner başlığı"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Açıklama
                        </label>
                        <textarea
                          value={editingBanner.description || ''}
                          onChange={(e) => setEditingBanner({ ...editingBanner, description: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                          placeholder="Banner açıklaması"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Görsel URL
                        </label>
                        <input
                          type="url"
                          value={editingBanner.imageUrl || ''}
                          onChange={(e) => setEditingBanner({ ...editingBanner, imageUrl: e.target.value })}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="https://example.com/banner.jpg"
                        />
                        {editingBanner.imageUrl && (
                          <div className="mt-2 rounded-lg overflow-hidden">
                            <img
                              src={editingBanner.imageUrl}
                              alt="Preview"
                              className="w-full h-32 object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Link URL (Opsiyonel)
                        </label>
                        <input
                          type="url"
                          value={editingBanner.linkUrl || ''}
                          onChange={(e) => setEditingBanner({ ...editingBanner, linkUrl: e.target.value })}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="https://example.com/promo"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Sıra
                        </label>
                        <input
                          type="number"
                          value={editingBanner.order || 0}
                          onChange={(e) => setEditingBanner({ ...editingBanner, order: parseInt(e.target.value) || 0 })}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          min="0"
                        />
                      </div>

                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="bannerActive"
                          checked={editingBanner.isActive}
                          onChange={(e) => setEditingBanner({ ...editingBanner, isActive: e.target.checked })}
                          className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
                        />
                        <label htmlFor="bannerActive" className="text-gray-300">
                          Aktif
                        </label>
                      </div>
                    </div>

                    <div className="flex space-x-3 mt-6">
                      <button
                        onClick={() => setIsBannerEditModalOpen(false)}
                        className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            if (editingBanner.id) {
                              await updateBanner(editingBanner.id, {
                                title: editingBanner.title,
                                description: editingBanner.description,
                                imageUrl: editingBanner.imageUrl,
                                linkUrl: editingBanner.linkUrl,
                                isActive: editingBanner.isActive,
                                order: editingBanner.order
                              });
                            } else {
                              await addBanner({
                                title: editingBanner.title,
                                description: editingBanner.description,
                                imageUrl: editingBanner.imageUrl,
                                linkUrl: editingBanner.linkUrl,
                                isActive: editingBanner.isActive,
                                order: editingBanner.order
                              });
                            }
                            setIsBannerEditModalOpen(false);
                            setEditingBanner(null);
                          } catch (error) {
                            console.error('Banner kaydedilirken hata:', error);
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>Kaydet</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bonus Kodları Tab */}
          {activeTab === 'bonusCodes' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white">Bonus Kodları Yönetimi</h3>
                  <button
                    onClick={() => {
                      setEditingBonusCode({
                        code: '',
                        amount: 0,
                        maxUsage: 1,
                        isActive: true,
                        description: ''
                      });
                      setIsBonusCodeEditModalOpen(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Kod Ekle</span>
                  </button>
                </div>

                {bonusCodes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-dark-700">
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Kod</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Bakiye (₺)</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Kullanım</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Durum</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Açıklama</th>
                          <th className="text-right py-3 px-4 text-gray-400 font-medium">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bonusCodes.map((code: any) => (
                          <tr key={code.id} className="border-b border-dark-700/50 hover:bg-dark-700/30">
                            <td className="py-3 px-4">
                              <span className="font-mono text-emerald-400 font-bold">{code.code}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-white font-semibold">{code.amount?.toLocaleString('tr-TR')} ₺</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-gray-300">{code.usageCount || 0} / {code.maxUsage || '∞'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                code.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {code.isActive ? 'Aktif' : 'Pasif'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-gray-400 text-sm">{code.description || '-'}</span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => {
                                    setEditingBonusCode(code);
                                    setIsBonusCodeEditModalOpen(true);
                                  }}
                                  className="p-2 text-primary-400 hover:text-primary-300 transition-colors"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Bu bonus kodunu silmek istediğinize emin misiniz?')) {
                                      await deleteBonusCode(code.id);
                                    }
                                  }}
                                  className="p-2 text-red-400 hover:text-red-300 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Gift className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg mb-2">Henüz bonus kodu eklenmemiş</p>
                    <p className="text-gray-500 text-sm">Yeni bir bonus kodu eklemek için yukarıdaki butonu kullanın</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Bonus Kodu Ekleme/Düzenleme Modal */}
          {isBonusCodeEditModalOpen && editingBonusCode && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-dark-800 rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">
                    {editingBonusCode.id ? 'Bonus Kodu Düzenle' : 'Yeni Bonus Kodu'}
                  </h3>
                  <button
                    onClick={() => {
                      setIsBonusCodeEditModalOpen(false);
                      setEditingBonusCode(null);
                    }}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      if (editingBonusCode.id) {
                        await updateBonusCode(editingBonusCode.id, editingBonusCode);
                      } else {
                        await addBonusCode(editingBonusCode);
                      }
                      setIsBonusCodeEditModalOpen(false);
                      setEditingBonusCode(null);
                    } catch (error) {
                      console.error('Bonus kodu kaydedilirken hata:', error);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bonus Kodu
                    </label>
                    <input
                      type="text"
                      value={editingBonusCode.code || ''}
                      onChange={(e) => setEditingBonusCode({...editingBonusCode, code: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm font-mono"
                      placeholder="Örn: DIMARIABET750"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bakiye Miktarı (₺)
                    </label>
                    <input
                      type="number"
                      value={editingBonusCode.amount || ''}
                      onChange={(e) => setEditingBonusCode({...editingBonusCode, amount: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                      placeholder="750"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Maksimum Kullanım (0 = Sınırsız)
                    </label>
                    <input
                      type="number"
                      value={editingBonusCode.maxUsage || ''}
                      onChange={(e) => setEditingBonusCode({...editingBonusCode, maxUsage: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                      placeholder="100"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Açıklama (Opsiyonel)
                    </label>
                    <input
                      type="text"
                      value={editingBonusCode.description || ''}
                      onChange={(e) => setEditingBonusCode({...editingBonusCode, description: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                      placeholder="Hoş geldin bonusu"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={editingBonusCode.isActive}
                      onChange={(e) => setEditingBonusCode({...editingBonusCode, isActive: e.target.checked})}
                      className="w-4 h-4 text-emerald-500 bg-dark-600 border-dark-500 rounded focus:ring-emerald-500"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-300">
                      Aktif
                    </label>
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setIsBonusCodeEditModalOpen(false);
                        setEditingBonusCode(null);
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                    >
                      {editingBonusCode.id ? 'Güncelle' : 'Ekle'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

      {/* Application Detail Modal */}
      <AnimatePresence>
        {isApplicationDetailOpen && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700/50">
              <h2 className="text-2xl font-bold text-white">Başvuru Detayları</h2>
              <button
                onClick={() => {
                  setIsApplicationDetailOpen(false);
                  setSelectedApplication(null);
                }}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Başvuru Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-dark-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-300">Kullanıcı Email</label>
                  <p className="text-white">{selectedApplication.userEmail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300">Durum</label>
                  <p className={`font-medium ${
                    selectedApplication.status === 'approved' ? 'text-green-400' :
                    selectedApplication.status === 'rejected' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {selectedApplication.status === 'approved' ? 'Onaylandı' :
                     selectedApplication.status === 'rejected' ? 'Reddedildi' : 'Beklemede'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300">Başvuru Tarihi</label>
                  <p className="text-white">
                    {selectedApplication.createdAt?.toDate ? 
                      selectedApplication.createdAt.toDate().toLocaleDateString('tr-TR') : 
                      'Tarih bilgisi yok'
                    }
                  </p>
                </div>
              </div>

              {/* Roleplay Soruları */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Roleplay Soruları</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Fail RP nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.failRp || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Meta Gaming Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.metaGaming || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      RDM Ve VDM Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.rdmVdm || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Non-RP Örnekleri veriniz.
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.nonRp || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      GOOA nedir? (Gun out of ass)
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.gooa || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      IC/OOC Nedir? Mixing Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.icOoc || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Revenge Kill Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.revengeKill || 'Cevap verilmemiş'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Karakter Bilgileri */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Karakter Bilgileri</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Karakterinizin Adı
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.characterName || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Karakterinizin Hikayesi
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white min-h-[100px]">
                      {selectedApplication.characterStory || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Karakterinizin Amacı
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white min-h-[100px]">
                      {selectedApplication.characterGoal || 'Cevap verilmemiş'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-4 p-6 border-t border-dark-700/50">
              <button
                onClick={() => {
                  setIsApplicationDetailOpen(false);
                  setSelectedApplication(null);
                }}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Kapat
              </button>
            </div>
          </motion.div>
        </div>
        )}
      </AnimatePresence>

      {/* Sponsor Detail Modal */}
      <AnimatePresence>
        {isSponsorDetailOpen && selectedSponsor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-dark-700/50">
                <h2 className="text-2xl font-bold text-white">Sponsor Detayları</h2>
                <button
                  onClick={() => {
                    setIsSponsorDetailOpen(false);
                    setSelectedSponsor(null);
                  }}
                  className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Sponsor Bilgileri */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-dark-700/50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Kullanıcı Email</label>
                    <p className="text-white">{selectedSponsor.userEmail}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">Durum</label>
                    <p className={`font-medium ${
                      selectedSponsor.status === 'approved' ? 'text-green-400' :
                      selectedSponsor.status === 'rejected' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {selectedSponsor.status === 'approved' ? 'Onaylandı' :
                       selectedSponsor.status === 'rejected' ? 'Reddedildi' : 'Beklemede'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">Başvuru Tarihi</label>
                    <p className="text-white">
                      {selectedSponsor.createdAt?.toDate ? 
                        selectedSponsor.createdAt.toDate().toLocaleDateString('tr-TR') : 
                        'Tarih bilgisi yok'
                      }
                    </p>
                  </div>
                </div>

                {/* Şirket Bilgileri */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Şirket Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Şirket Adı</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.companyName}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Şirket Türü</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.companyType}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.website || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">E-posta</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.email}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Telefon</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.phone || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Adres</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.address || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* İletişim Bilgileri */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">İletişim Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">İletişim Kişisi</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactPerson}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Pozisyon</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactTitle || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">İletişim E-postası</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactEmail}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">İletişim Telefonu</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactPhone || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sponsorluk Detayları */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Sponsorluk Detayları</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sponsorluk Türü</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.sponsorshipType}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Bütçe Aralığı</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.budget}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Süre</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.duration} Ay
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Hedef Kitle</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.targetAudience || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Beklenen Erişim</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.expectedReach || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ek Bilgiler */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Ek Bilgiler</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Önceki Sponsorluk Deneyimi</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.previousSponsorship || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Pazarlama Hedefleri</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.marketingGoals || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Özel Gereksinimler</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.specialRequirements || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Ek Bilgiler</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.additionalInfo || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedSponsor.status === 'pending' && (
                  <div className="flex justify-end space-x-4 pt-6 border-t border-dark-700/50">
                    <button
                      onClick={() => {
                        updateSponsorStatus(selectedSponsor.id, 'approved');
                        setIsSponsorDetailOpen(false);
                        setSelectedSponsor(null);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      <Check className="w-4 h-4" />
                      <span>Onayla</span>
                    </button>
                    <button
                      onClick={() => {
                        updateSponsorStatus(selectedSponsor.id, 'rejected');
                        setIsSponsorDetailOpen(false);
                        setSelectedSponsor(null);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Reddet</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Ban/Timeout Pop-up Modal */}
      <AnimatePresence>
        {showBanTimeoutPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowBanTimeoutPanel(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-dark-800/95 backdrop-blur-md border border-dark-700/50 rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">Ban ve Timeout Yönetimi</h3>
                  <button
                    onClick={() => setShowBanTimeoutPanel(false)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex space-x-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Kullanıcı ara..."
                          value={banTimeoutSearchTerm}
                          onChange={(e) => setBanTimeoutSearchTerm(e.target.value)}
                          className="w-64 px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Banlanan Kullanıcılar */}
                  <div className="mb-6">
                    <h5 className="text-md font-medium text-white mb-3 flex items-center space-x-2">
                      <Ban className="w-4 h-4 text-red-400" />
                      <span>Banlanan Kullanıcılar ({bannedUsers.length})</span>
                    </h5>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {bannedUsers
                        .filter(user => {
                          const matchesSearch = user.displayName?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.email?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.banReason?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase());
                          return matchesSearch;
                        })
                        .map((user: any) => (
                        <div key={user.id} className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                                <Ban className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-white font-medium">{user.displayName}</h4>
                                <p className="text-sm text-gray-400">{user.email}</p>
                                <p className="text-xs text-red-400">
                                  Ban Sebebi: {user.banReason || 'Belirtilmemiş'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Ban Tarihi: {user.bannedAt ? new Date(user.bannedAt.seconds * 1000).toLocaleString('tr-TR') : 'Bilinmeyen'}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => unbanUser(user.id)}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                                title="Banı Aç"
                              >
                                <UserCheck className="w-4 h-4" />
                                <span>Banı Aç</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {bannedUsers.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <Ban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Banlanan kullanıcı bulunmuyor</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Timeout Edilen Kullanıcılar */}
                  <div>
                    <h5 className="text-md font-medium text-white mb-3 flex items-center space-x-2">
                      <Timer className="w-4 h-4 text-yellow-400" />
                      <span>Timeout Edilen Kullanıcılar ({timeoutUsers.length})</span>
                    </h5>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {timeoutUsers
                        .filter(user => {
                          const matchesSearch = user.displayName?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.email?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.reason?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase());
                          return matchesSearch;
                        })
                        .map((user: any) => (
                        <div key={user.id} className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                                <Timer className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-white font-medium">{user.displayName}</h4>
                                <p className="text-sm text-gray-400">{user.email || 'Email bulunamadı'}</p>
                                <p className="text-xs text-yellow-400">
                                  Timeout Sebebi: {user.reason || 'Belirtilmemiş'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Kalan Süre: {user.remainingTime} dakika
                                </p>
                                <p className="text-xs text-gray-500">
                                  Timeout Tarihi: {user.timeoutAt ? new Date(user.timeoutAt).toLocaleString('tr-TR') : 'Bilinmeyen'}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => removeTimeout(user.id)}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                                title="Timeout'u Kaldır"
                              >
                                <UserCheck className="w-4 h-4" />
                                <span>Timeout Kaldır</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {timeoutUsers.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <Timer className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Timeout edilen kullanıcı bulunmuyor</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

        {/* Product Edit Modal */}
        <AnimatePresence>
          {isProductEditModalOpen && editingProduct && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setIsProductEditModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-dark-800 border border-dark-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">
                    {editingProduct.id ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
                  </h3>
                  <button
                    onClick={() => setIsProductEditModalOpen(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Ürün Adı</label>
                      <input
                        type="text"
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ürün adını girin"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Kategori</label>
                      <select
                        value={editingProduct.category}
                        onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="vehicles">Araçlar</option>
                        <option value="clothing">Kıyafetler</option>
                        <option value="weapons">Silahlar</option>
                        <option value="properties">Mülkler</option>
                        <option value="accessories">Aksesuarlar</option>
                        <option value="games">Oyunlar</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Açıklama</label>
                    <textarea
                      value={editingProduct.description}
                      onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                      placeholder="Ürün açıklamasını girin"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Fiyat</label>
                      <input
                        type="number"
                        value={editingProduct.price}
                        onChange={(e) => setEditingProduct({...editingProduct, price: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Para Birimi</label>
                      <select
                        value={editingProduct.currency}
                        onChange={(e) => setEditingProduct({...editingProduct, currency: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="TL">TL</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Versiyon</label>
                      <input
                        type="text"
                        value={editingProduct.version}
                        onChange={(e) => setEditingProduct({...editingProduct, version: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="1.0.0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Resim URL</label>
                    <input
                      type="url"
                      value={editingProduct.image}
                      onChange={(e) => setEditingProduct({...editingProduct, image: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Özellikler (Her satıra bir özellik)</label>
                    <textarea
                      value={editingProduct.features.join('\n')}
                      onChange={(e) => setEditingProduct({...editingProduct, features: e.target.value.split('\n').filter(f => f.trim())})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={4}
                      placeholder="Özellik 1&#10;Özellik 2&#10;Özellik 3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Sunucu</label>
                    <input
                      type="text"
                      value={editingProduct.server}
                      onChange={(e) => setEditingProduct({...editingProduct, server: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Sunucu adını girin (örn: Aedon RP #1)"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Geliştirici</label>
                      <input
                        type="text"
                        value={editingProduct.author}
                        onChange={(e) => setEditingProduct({...editingProduct, author: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Geliştirici adı"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Puan</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        step="0.1"
                        value={editingProduct.rating}
                        onChange={(e) => setEditingProduct({...editingProduct, rating: parseFloat(e.target.value) || 5.0})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Oyuncu Sayısı</label>
                      <input
                        type="number"
                        min="0"
                        value={editingProduct.players}
                        onChange={(e) => setEditingProduct({...editingProduct, players: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-dark-700/50">
                  <button
                    onClick={() => setIsProductEditModalOpen(false)}
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        if (editingProduct.id) {
                          await updateMarketProduct(editingProduct.id, editingProduct);
                        } else {
                          await addMarketProduct(editingProduct);
                        }
                        setIsProductEditModalOpen(false);
                        setEditingProduct(null);
                      } catch (error) {
                        console.error('Ürün kaydedilirken hata:', error);
                      }
                    }}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>{editingProduct.id ? 'Güncelle' : 'Ekle'}</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

          {/* Game Management Tab */}
          {activeTab === 'games' && hasAccess(userRole, 'games') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Gamepad2 className="w-6 h-6 text-primary-400" />
                    <span>Oyun Yönetimi</span>
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        loadLobbyGames('slots');
                        setSelectedHomeLobbyGames(new Set());
                        setHomeLobbySearchTerm('');
                        setIsHomeLobbySelectModalOpen(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg transition-colors shadow-lg shadow-purple-500/25"
                    >
                      <Dices className="w-4 h-4" />
                      <span>Slots Lobisi</span>
                    </button>
                    <button
                      onClick={() => {
                        loadLobbyGames('casino');
                        setSelectedHomeLobbyGames(new Set());
                        setHomeLobbySearchTerm('');
                        setIsHomeLobbySelectModalOpen(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-colors shadow-lg shadow-green-500/25"
                    >
                      <Gamepad2 className="w-4 h-4" />
                      <span>Casino Lobisi</span>
                    </button>
                    <button
                      onClick={() => {
                        setEditingGame({
                          name: '',
                          imageUrl: '',
                          provider: '',
                          rating: 4.5,
                          playCount: 0,
                          commentCount: 0,
                          trend: 'stable',
                          order: games.length + 1,
                          isPopular: true,
                          isActive: true
                        });
                        setIsGameEditModalOpen(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Oyun Ekle</span>
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Oyun ara..."
                      value={gameSearchTerm}
                      onChange={(e) => setGameSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Popular Games Section */}
                <div className="mb-8">
                  <h4 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    <span>En Popüler Oyunlar (Anasayfa)</span>
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Anasayfada görüntülenecek popüler oyunlar. Düzenlemek veya silmek için kartların üzerine gelin.
                  </p>

                  {games.filter(g => g.isPopular).length === 0 ? (
                    <div className="text-center py-12">
                      <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 text-lg">Henüz oyun eklenmemiş</p>
                      <p className="text-gray-500 text-sm mt-2">Yukarıdaki "Slots Lobisi" veya "Casino Lobisi" butonlarına tıklayarak oyun ekleyebilirsiniz.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {games
                        .filter(g => g.isPopular)
                        .filter(g => !gameSearchTerm || g.name?.toLowerCase().includes(gameSearchTerm.toLowerCase()) || g.provider?.toLowerCase().includes(gameSearchTerm.toLowerCase()))
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((game, index) => (
                          <motion.div
                            key={game.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="bg-dark-700/50 rounded-xl overflow-hidden border border-dark-600/50 hover:border-primary-500/50 transition-all group"
                          >
                            {game.imageUrl && (
                              <div className="relative h-32 overflow-hidden">
                                <img
                                  src={game.imageUrl}
                                  alt={game.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent" />
                                <div className="absolute top-2 right-2 bg-orange-500/90 text-white text-xs px-2 py-1 rounded-full">
                                  #{game.order || index + 1}
                                </div>
                                {game.rating && (
                                  <div className="absolute top-2 left-2 bg-yellow-500/90 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                                    <Star className="w-3 h-3 fill-current" />
                                    <span>{game.rating}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="p-4">
                              <h4 className="text-white font-semibold mb-1 truncate">{game.name}</h4>
                              {game.provider && (
                                <p className="text-gray-500 text-sm mb-2">{game.provider}</p>
                              )}
                              <div className="flex items-center justify-between mt-3">
                                <span className={`text-xs px-2 py-1 rounded-full ${game.isActive !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                  {game.isActive !== false ? 'Aktif' : 'Pasif'}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      setEditingGame(game);
                                      setIsGameEditModalOpen(true);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (window.confirm('Bu oyunu silmek istediğinizden emin misiniz?')) {
                                        await deleteDoc(doc(db, 'populerOyunlar', game.id));
                                        loadGames();
                                      }
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Most Talked Games Section */}
                <div>
                  <h4 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    <span>En Çok Konuşulan Oyunlar</span>
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {games
                      .filter(g => !gameSearchTerm || g.name.toLowerCase().includes(gameSearchTerm.toLowerCase()))
                      .map((game) => (
                        <div
                          key={game.id}
                          className="p-4 bg-dark-700/50 rounded-lg border border-dark-600"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <img
                              src={game.imageUrl || 'https://via.placeholder.com/50x50'}
                              alt={game.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                              <p className="text-white font-medium">{game.name}</p>
                              <p className="text-gray-400 text-sm">{game.provider}</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-2 text-gray-400">
                              <MessageCircle className="w-4 h-4" />
                              <span>{game.commentCount || 0} yorum</span>
                            </div>
                            <div className={`flex items-center space-x-1 ${
                              game.trend === 'up' ? 'text-green-400' :
                              game.trend === 'down' ? 'text-red-400' : 'text-gray-400'
                            }`}>
                              <TrendingUp className="w-4 h-4" />
                              <span className="capitalize">{game.trend || 'stable'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Game Edit Modal */}
          <AnimatePresence>
            {isGameEditModalOpen && editingGame && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setIsGameEditModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-dark-800 rounded-xl p-6 w-full max-w-lg border border-dark-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-semibold text-white mb-6">
                    {editingGame.id ? 'Oyun Düzenle' : 'Yeni Oyun Ekle'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Oyun Adı</label>
                      <input
                        type="text"
                        value={editingGame.name}
                        onChange={(e) => setEditingGame({ ...editingGame, name: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="Sweet Bonanza"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Görsel URL</label>
                      <input
                        type="text"
                        value={editingGame.imageUrl}
                        onChange={(e) => setEditingGame({ ...editingGame, imageUrl: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="https://..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sağlayıcı</label>
                      <input
                        type="text"
                        value={editingGame.provider}
                        onChange={(e) => setEditingGame({ ...editingGame, provider: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="Pragmatic Play"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Yıldız (1-5)</label>
                        <input
                          type="number"
                          value={editingGame.rating}
                          onChange={(e) => setEditingGame({ ...editingGame, rating: parseFloat(e.target.value) })}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                          min="1"
                          max="5"
                          step="0.1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Sıra</label>
                        <input
                          type="number"
                          value={editingGame.order}
                          onChange={(e) => setEditingGame({ ...editingGame, order: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingGame.isPopular}
                          onChange={(e) => setEditingGame({ ...editingGame, isPopular: e.target.checked })}
                          className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-primary-500"
                        />
                        <span className="text-gray-300">Popüler Oyun</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingGame.isActive}
                          onChange={(e) => setEditingGame({ ...editingGame, isActive: e.target.checked })}
                          className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-primary-500"
                        />
                        <span className="text-gray-300">Aktif</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={() => setIsGameEditModalOpen(false)}
                      className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg"
                    >
                      İptal
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          if (editingGame.id) {
                            await updateDoc(doc(db, 'populerOyunlar', editingGame.id), {
                              name: editingGame.name,
                              imageUrl: editingGame.imageUrl,
                              provider: editingGame.provider,
                              rating: editingGame.rating,
                              order: editingGame.order,
                              isPopular: editingGame.isPopular,
                              isActive: editingGame.isActive,
                              playCount: editingGame.playCount || 0,
                              commentCount: editingGame.commentCount || 0,
                              trend: editingGame.trend || 'stable'
                            });
                          } else {
                            await addDoc(collection(db, 'populerOyunlar'), {
                              ...editingGame,
                              createdAt: serverTimestamp()
                            });
                          }
                          setIsGameEditModalOpen(false);
                          setEditingGame(null);
                          loadGames();
                        } catch (error) {
                          console.error('Oyun kaydedilirken hata:', error);
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg flex items-center justify-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Kaydet</span>
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Promo Management Tab */}
          {activeTab === 'promo' && hasAccess(userRole, 'promo') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Gift className="w-6 h-6 text-pink-400" />
                    <span>Promosyon Yönetimi</span>
                  </h3>
                  <button
                    onClick={() => {
                      setEditingPromo({
                        title: '',
                        description: '',
                        imageUrl: '',
                        order: promotions.length + 1,
                        isActive: true
                      });
                      setIsPromoEditModalOpen(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Promosyon</span>
                  </button>
                </div>

                {promotions.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Henüz promosyon eklenmemiş</p>
                    <p className="text-gray-500 text-sm mt-2">Yeni bir promosyon eklemek için yukarıdaki butona tıklayın.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {promotions.map((promo) => (
                      <div
                        key={promo.id}
                        className="bg-dark-700/50 rounded-xl border border-dark-600/50 overflow-hidden hover:border-pink-500/50 transition-all duration-300"
                      >
                        {promo.imageUrl && (
                          <div className="relative h-40">
                            <img
                              src={promo.imageUrl}
                              alt={promo.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-2 right-2 flex space-x-1">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                promo.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {promo.isActive ? 'Aktif' : 'Pasif'}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="p-4">
                          <h4 className="text-white font-semibold mb-2">{promo.title}</h4>
                          <p className="text-gray-400 text-sm line-clamp-2 mb-4">{promo.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-xs">Sıra: {promo.order}</span>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setEditingPromo(promo);
                                  setIsPromoEditModalOpen(true);
                                }}
                                className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Bu promosyonu silmek istediğinize emin misiniz?')) {
                                    await deletePromotion(promo.id);
                                  }
                                }}
                                className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Promo Edit Modal */}
          <AnimatePresence>
            {isPromoEditModalOpen && editingPromo && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setIsPromoEditModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-dark-800 rounded-xl p-6 w-full max-w-lg border border-dark-700 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-semibold text-white mb-6">
                    {editingPromo.id ? 'Promosyon Düzenle' : 'Yeni Promosyon Ekle'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Başlık</label>
                      <input
                        type="text"
                        value={editingPromo.title}
                        onChange={(e) => setEditingPromo({ ...editingPromo, title: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="Promosyon başlığı"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Görsel URL</label>
                      <input
                        type="text"
                        value={editingPromo.imageUrl}
                        onChange={(e) => setEditingPromo({ ...editingPromo, imageUrl: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="https://..."
                      />
                      {editingPromo.imageUrl && (
                        <div className="mt-2 rounded-lg overflow-hidden">
                          <img src={editingPromo.imageUrl} alt="Preview" className="w-full h-32 object-cover" />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Açıklama (Metin Düzenleyici)</label>
                      <RichTextEditor
                        value={editingPromo.description}
                        onChange={(value) => setEditingPromo({ ...editingPromo, description: value })}
                        placeholder="Promosyon açıklaması..."
                        minHeight="150px"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sıra</label>
                      <input
                        type="number"
                        value={editingPromo.order}
                        onChange={(e) => setEditingPromo({ ...editingPromo, order: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        min="1"
                      />
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingPromo.isActive}
                          onChange={(e) => setEditingPromo({ ...editingPromo, isActive: e.target.checked })}
                          className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-pink-500"
                        />
                        <span className="text-gray-300">Aktif</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={() => setIsPromoEditModalOpen(false)}
                      className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg"
                    >
                      İptal
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          if (editingPromo.id) {
                            await updatePromotion(editingPromo.id, {
                              title: editingPromo.title,
                              description: editingPromo.description,
                              imageUrl: editingPromo.imageUrl,
                              order: editingPromo.order,
                              isActive: editingPromo.isActive
                            });
                          } else {
                            await addPromotion({
                              title: editingPromo.title,
                              description: editingPromo.description,
                              imageUrl: editingPromo.imageUrl,
                              order: editingPromo.order,
                              isActive: editingPromo.isActive
                            });
                          }
                          setIsPromoEditModalOpen(false);
                          setEditingPromo(null);
                        } catch (error) {
                          console.error('Promosyon kaydedilirken hata:', error);
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-lg flex items-center justify-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Kaydet</span>
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tournaments Management Tab */}
          {activeTab === 'tournaments' && hasAccess(userRole, 'tournaments') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Trophy className="w-6 h-6 text-amber-400" />
                    <span>Turnuva Yönetimi</span>
                  </h3>
                  <button
                    onClick={() => {
                      setEditingTournament({
                        title: '',
                        description: '',
                        imageUrl: '',
                        order: tournaments.length + 1,
                        isActive: true
                      });
                      setIsTournamentEditModalOpen(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Turnuva</span>
                  </button>
                </div>

                {tournaments.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Henüz turnuva eklenmemiş</p>
                    <p className="text-gray-500 text-sm mt-2">Yeni bir turnuva eklemek için yukarıdaki butona tıklayın.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tournaments.map((tournament) => (
                      <div
                        key={tournament.id}
                        className="bg-dark-700/50 rounded-xl border border-dark-600/50 overflow-hidden hover:border-amber-500/50 transition-all duration-300"
                      >
                        {tournament.imageUrl && (
                          <div className="relative h-40">
                            <img
                              src={tournament.imageUrl}
                              alt={tournament.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-2 right-2 flex space-x-1">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                tournament.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {tournament.isActive ? 'Aktif' : 'Pasif'}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="p-4">
                          <h4 className="text-white font-semibold mb-2">{tournament.title}</h4>
                          <p className="text-gray-400 text-sm line-clamp-2 mb-4">{tournament.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-xs">Sıra: {tournament.order}</span>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setEditingTournament(tournament);
                                  setIsTournamentEditModalOpen(true);
                                }}
                                className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Bu turnuvayı silmek istediğinize emin misiniz?')) {
                                    await deleteTournament(tournament.id);
                                  }
                                }}
                                className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Tournament Edit Modal */}
          <AnimatePresence>
            {isTournamentEditModalOpen && editingTournament && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setIsTournamentEditModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-dark-800 rounded-xl p-6 w-full max-w-lg border border-dark-700 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-semibold text-white mb-6">
                    {editingTournament.id ? 'Turnuva Düzenle' : 'Yeni Turnuva Ekle'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Başlık</label>
                      <input
                        type="text"
                        value={editingTournament.title}
                        onChange={(e) => setEditingTournament({ ...editingTournament, title: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="Turnuva başlığı"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Görsel URL</label>
                      <input
                        type="text"
                        value={editingTournament.imageUrl}
                        onChange={(e) => setEditingTournament({ ...editingTournament, imageUrl: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        placeholder="https://..."
                      />
                      {editingTournament.imageUrl && (
                        <div className="mt-2 rounded-lg overflow-hidden">
                          <img src={editingTournament.imageUrl} alt="Preview" className="w-full h-32 object-cover" />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Açıklama (Metin Düzenleyici)</label>
                      <RichTextEditor
                        value={editingTournament.description}
                        onChange={(value) => setEditingTournament({ ...editingTournament, description: value })}
                        placeholder="Turnuva açıklaması..."
                        minHeight="150px"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sıra</label>
                      <input
                        type="number"
                        value={editingTournament.order}
                        onChange={(e) => setEditingTournament({ ...editingTournament, order: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white"
                        min="1"
                      />
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingTournament.isActive}
                          onChange={(e) => setEditingTournament({ ...editingTournament, isActive: e.target.checked })}
                          className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-amber-500"
                        />
                        <span className="text-gray-300">Aktif</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={() => setIsTournamentEditModalOpen(false)}
                      className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg"
                    >
                      İptal
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          if (editingTournament.id) {
                            await updateTournament(editingTournament.id, {
                              title: editingTournament.title,
                              description: editingTournament.description,
                              imageUrl: editingTournament.imageUrl,
                              order: editingTournament.order,
                              isActive: editingTournament.isActive
                            });
                          } else {
                            await addTournament({
                              title: editingTournament.title,
                              description: editingTournament.description,
                              imageUrl: editingTournament.imageUrl,
                              order: editingTournament.order,
                              isActive: editingTournament.isActive
                            });
                          }
                          setIsTournamentEditModalOpen(false);
                          setEditingTournament(null);
                        } catch (error) {
                          console.error('Turnuva kaydedilirken hata:', error);
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg flex items-center justify-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Kaydet</span>
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Marketing Tab */}
          {activeTab === 'marketing' && hasAccess(userRole, 'marketing') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Megaphone className="w-6 h-6 text-cyan-400" />
                    <span>Marketing Yönetimi</span>
                  </h3>
                </div>

                <div className="text-center py-12">
                  <Megaphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">Marketing yönetimi yakında eklenecek</p>
                  <p className="text-gray-500 text-sm mt-2">Bu bölümde pazarlama kampanyaları ve analizleri yönetebileceksiniz.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Risk Management Tab */}
          {activeTab === 'risk' && hasAccess(userRole, 'risk') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                    <span>Risk Yönetimi</span>
                  </h3>
                </div>

                <div className="text-center py-12">
                  <AlertTriangle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">Risk yönetimi yakında eklenecek</p>
                  <p className="text-gray-500 text-sm mt-2">Bu bölümde risk analizi ve kullanıcı güvenliği yönetebileceksiniz.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Pages Management Tab */}
          {activeTab === 'pages' && hasAccess(userRole, 'pages') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Layers className="w-6 h-6 text-indigo-400" />
                    <span>
                      Sayfa Yönetimi
                      {selectedPageType && (
                        <span className="text-indigo-400 ml-2">
                          - {selectedPageType.includes('slots_')
                            ? selectedPageType === 'slots_lobby' ? 'Slots Lobby'
                            : selectedPageType === 'slots_popular' ? 'Popüler Oyunlar'
                            : selectedPageType === 'slots_new' ? 'Yeni Çıkanlar'
                            : 'FreeSpinler'
                            : selectedPageType === 'casino_lobby' ? 'Canlı Lobi'
                            : selectedPageType === 'casino_table' ? 'Masa Oyunları'
                            : selectedPageType === 'casino_roulette' ? 'Rulet'
                            : 'Blackjack'
                          }
                        </span>
                      )}
                    </span>
                  </h3>
                  {selectedPageType && (
                    <div className="flex items-center space-x-2">
                      {/* Lobiden Seç butonu - sadece lobby olmayan sayfalarda */}
                      {!selectedPageType.endsWith('_lobby') && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            const type = selectedPageType.startsWith('slots_') ? 'slots' : 'casino';
                            loadLobbyGames(type);
                            setSelectedLobbyGames(new Set());
                            setLobbySearchTerm('');
                            setIsLobbySelectModalOpen(true);
                          }}
                          className={`flex items-center space-x-2 text-white px-4 py-2 rounded-lg transition-colors shadow-lg ${
                            selectedPageType.startsWith('slots_')
                              ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-purple-500/25'
                              : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-green-500/25'
                          }`}
                        >
                          {selectedPageType.startsWith('slots_') ? (
                            <Dices className="w-4 h-4" />
                          ) : (
                            <Gamepad2 className="w-4 h-4" />
                          )}
                          <span>{selectedPageType.startsWith('slots_') ? 'Slots Lobisinden Seç' : 'Casino Lobisinden Seç'}</span>
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setEditingPageGame(null);
                          setIsPageGameEditModalOpen(true);
                        }}
                        className="flex items-center space-x-2 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-lg shadow-indigo-500/25"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Oyun Ekle</span>
                      </motion.button>
                    </div>
                  )}
                </div>

                {!selectedPageType ? (
                  <div className="text-center py-12">
                    <Layers className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Sol menüden bir sayfa seçin</p>
                    <p className="text-gray-500 text-sm mt-2">Slotlar veya Canlı Casino altından düzenlemek istediğiniz sayfayı seçin.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pageGames.length === 0 ? (
                      <div className="text-center py-12">
                        <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">Henüz oyun eklenmemiş</p>
                        <p className="text-gray-500 text-sm mt-2">Yukarıdaki "Oyun Ekle" butonuna tıklayarak oyun ekleyebilirsiniz.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {pageGames.map((game, index) => (
                          <motion.div
                            key={game.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="bg-dark-700/50 rounded-xl overflow-hidden border border-dark-600/50 hover:border-indigo-500/50 transition-all group"
                          >
                            {game.imageUrl && (
                              <div className="relative h-32 overflow-hidden">
                                <img
                                  src={game.imageUrl}
                                  alt={game.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent" />
                                <div className="absolute top-2 right-2 bg-indigo-500/90 text-white text-xs px-2 py-1 rounded-full">
                                  #{game.order}
                                </div>
                              </div>
                            )}
                            <div className="p-4">
                              <h4 className="text-white font-semibold mb-1 truncate">{game.name}</h4>
                              {game.provider && (
                                <p className="text-gray-500 text-sm mb-2">{game.provider}</p>
                              )}
                              <div className="flex items-center justify-between mt-3">
                                <span className={`text-xs px-2 py-1 rounded-full ${game.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                  {game.isActive ? 'Aktif' : 'Pasif'}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      setEditingPageGame(game);
                                      setIsPageGameEditModalOpen(true);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-colors"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deletePageGame(game.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Page Game Edit/Add Modal */}
          <AnimatePresence>
            {isPageGameEditModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                style={{ zIndex: 99999 }}
                onClick={() => setIsPageGameEditModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-dark-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-dark-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-6">
                      {editingPageGame ? 'Oyun Düzenle' : 'Yeni Oyun Ekle'}
                    </h3>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const gameData = {
                          name: formData.get('name'),
                          provider: formData.get('provider'),
                          imageUrl: formData.get('imageUrl'),
                          gameUrl: formData.get('gameUrl'),
                          order: parseInt(formData.get('order') as string) || 0,
                          isActive: formData.get('isActive') === 'on'
                        };
                        try {
                          if (editingPageGame) {
                            await updatePageGame(editingPageGame.id, gameData);
                          } else {
                            await addPageGame(gameData);
                          }
                          setIsPageGameEditModalOpen(false);
                        } catch (error) {
                          console.error('Oyun kaydedilirken hata:', error);
                        }
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Oyun Adı</label>
                        <input
                          type="text"
                          name="name"
                          defaultValue={editingPageGame?.name || ''}
                          required
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none"
                          placeholder="Örn: Sweet Bonanza"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Sağlayıcı</label>
                        <input
                          type="text"
                          name="provider"
                          defaultValue={editingPageGame?.provider || ''}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none"
                          placeholder="Örn: Pragmatic Play"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Görsel URL</label>
                        <input
                          type="url"
                          name="imageUrl"
                          defaultValue={editingPageGame?.imageUrl || ''}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Oyun URL</label>
                        <input
                          type="url"
                          name="gameUrl"
                          defaultValue={editingPageGame?.gameUrl || ''}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Sıra</label>
                        <input
                          type="number"
                          name="order"
                          defaultValue={editingPageGame?.order || pageGames.length + 1}
                          min="1"
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          name="isActive"
                          id="isActive"
                          defaultChecked={editingPageGame?.isActive ?? true}
                          className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-indigo-500 focus:ring-indigo-500"
                        />
                        <label htmlFor="isActive" className="text-gray-400 text-sm">Aktif</label>
                      </div>
                      <div className="flex items-center space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setIsPageGameEditModalOpen(false)}
                          className="flex-1 px-4 py-2.5 bg-dark-700 text-gray-300 rounded-lg hover:bg-dark-600 transition-colors"
                        >
                          İptal
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-lg hover:from-indigo-600 hover:to-blue-700 transition-colors"
                        >
                          {editingPageGame ? 'Güncelle' : 'Ekle'}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lobiden Oyun Seçme Modalı */}
          <AnimatePresence>
            {isLobbySelectModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                style={{ zIndex: 99999 }}
                onClick={() => setIsLobbySelectModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-dark-800 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden border border-dark-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6 border-b border-dark-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                        <Layers className="w-6 h-6 text-green-400" />
                        <span>Lobiden Oyun Seç</span>
                      </h3>
                      <button
                        onClick={() => setIsLobbySelectModalOpen(false)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          value={lobbySearchTerm}
                          onChange={(e) => setLobbySearchTerm(e.target.value)}
                          placeholder="Oyun ara..."
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-10 pr-4 py-2.5 text-white focus:border-green-500 focus:outline-none"
                        />
                      </div>
                      <div className="text-sm text-gray-400">
                        {selectedLobbyGames.size} oyun seçildi
                      </div>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
                    {lobbyGames.length === 0 ? (
                      <div className="text-center py-12">
                        <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">Lobby'de oyun bulunamadı</p>
                        <p className="text-gray-500 text-sm mt-2">Önce Lobby sayfasına oyun eklemeniz gerekiyor.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {lobbyGames
                          .filter(game =>
                            game.name?.toLowerCase().includes(lobbySearchTerm.toLowerCase()) ||
                            game.provider?.toLowerCase().includes(lobbySearchTerm.toLowerCase())
                          )
                          .map((game) => {
                            const isSelected = selectedLobbyGames.has(game.id);
                            const isAlreadyAdded = pageGames.some(pg => pg.name === game.name);
                            return (
                              <motion.div
                                key={game.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                  if (isAlreadyAdded) return;
                                  const newSet = new Set(selectedLobbyGames);
                                  if (isSelected) {
                                    newSet.delete(game.id);
                                  } else {
                                    newSet.add(game.id);
                                  }
                                  setSelectedLobbyGames(newSet);
                                }}
                                className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                                  isAlreadyAdded
                                    ? 'border-yellow-500/50 opacity-50 cursor-not-allowed'
                                    : isSelected
                                    ? 'border-green-500 shadow-lg shadow-green-500/25'
                                    : 'border-dark-600/50 hover:border-green-500/50'
                                }`}
                              >
                                {game.imageUrl && (
                                  <div className="relative h-28 overflow-hidden">
                                    <img
                                      src={game.imageUrl}
                                      alt={game.name}
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 to-transparent" />
                                  </div>
                                )}
                                <div className="p-3">
                                  <h4 className="text-white text-sm font-semibold truncate">{game.name}</h4>
                                  {game.provider && (
                                    <p className="text-gray-500 text-xs truncate">{game.provider}</p>
                                  )}
                                </div>
                                {/* Seçim göstergesi */}
                                <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                                  isAlreadyAdded
                                    ? 'bg-yellow-500/90'
                                    : isSelected
                                    ? 'bg-green-500'
                                    : 'bg-dark-800/80 border border-dark-600'
                                }`}>
                                  {isAlreadyAdded ? (
                                    <Check className="w-4 h-4 text-white" />
                                  ) : isSelected ? (
                                    <Check className="w-4 h-4 text-white" />
                                  ) : null}
                                </div>
                                {isAlreadyAdded && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-yellow-500/90 text-center py-1">
                                    <span className="text-xs text-white font-medium">Zaten Eklendi</span>
                                  </div>
                                )}
                              </motion.div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  <div className="p-4 border-t border-dark-700 flex items-center justify-between">
                    <button
                      onClick={() => {
                        // Tümünü seç/kaldır
                        if (selectedLobbyGames.size === lobbyGames.filter(g => !pageGames.some(pg => pg.name === g.name)).length) {
                          setSelectedLobbyGames(new Set());
                        } else {
                          const newSet = new Set<string>();
                          lobbyGames.forEach(g => {
                            if (!pageGames.some(pg => pg.name === g.name)) {
                              newSet.add(g.id);
                            }
                          });
                          setSelectedLobbyGames(newSet);
                        }
                      }}
                      className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                    >
                      {selectedLobbyGames.size === lobbyGames.filter(g => !pageGames.some(pg => pg.name === g.name)).length
                        ? 'Tümünü Kaldır'
                        : 'Tümünü Seç'}
                    </button>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setIsLobbySelectModalOpen(false)}
                        className="px-4 py-2.5 bg-dark-700 text-gray-300 rounded-lg hover:bg-dark-600 transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        onClick={() => addGamesFromLobby(selectedLobbyGames)}
                        disabled={selectedLobbyGames.size === 0}
                        className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>{selectedLobbyGames.size} Oyun Ekle</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Anasayfa Popüler Oyunlar için Lobiden Seçme Modalı */}
          <AnimatePresence>
            {isHomeLobbySelectModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                style={{ zIndex: 99999 }}
                onClick={() => setIsHomeLobbySelectModalOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-dark-800 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden border border-dark-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6 border-b border-dark-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                        <Flame className="w-6 h-6 text-orange-500" />
                        <span>Lobiden Popüler Oyunlara Ekle</span>
                      </h3>
                      <button
                        onClick={() => setIsHomeLobbySelectModalOpen(false)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          value={homeLobbySearchTerm}
                          onChange={(e) => setHomeLobbySearchTerm(e.target.value)}
                          placeholder="Oyun ara..."
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-10 pr-4 py-2.5 text-white focus:border-orange-500 focus:outline-none"
                        />
                      </div>
                      <div className="text-sm text-gray-400">
                        {selectedHomeLobbyGames.size} oyun seçildi
                      </div>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
                    {lobbyGames.length === 0 ? (
                      <div className="text-center py-12">
                        <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">Lobby'de oyun bulunamadı</p>
                        <p className="text-gray-500 text-sm mt-2">Önce Sayfalar {'>'} Slotlar {'>'} Lobby'e oyun eklemeniz gerekiyor.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {lobbyGames
                          .filter(game =>
                            game.name?.toLowerCase().includes(homeLobbySearchTerm.toLowerCase()) ||
                            game.provider?.toLowerCase().includes(homeLobbySearchTerm.toLowerCase())
                          )
                          .map((game) => {
                            const isSelected = selectedHomeLobbyGames.has(game.id);
                            const isAlreadyAdded = games.some(g => g.name === game.name);
                            return (
                              <motion.div
                                key={game.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                  if (isAlreadyAdded) return;
                                  const newSet = new Set(selectedHomeLobbyGames);
                                  if (isSelected) {
                                    newSet.delete(game.id);
                                  } else {
                                    newSet.add(game.id);
                                  }
                                  setSelectedHomeLobbyGames(newSet);
                                }}
                                className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                                  isAlreadyAdded
                                    ? 'border-yellow-500/50 opacity-50 cursor-not-allowed'
                                    : isSelected
                                    ? 'border-orange-500 shadow-lg shadow-orange-500/25'
                                    : 'border-dark-600/50 hover:border-orange-500/50'
                                }`}
                              >
                                {game.imageUrl && (
                                  <div className="relative h-28 overflow-hidden">
                                    <img
                                      src={game.imageUrl}
                                      alt={game.name}
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 to-transparent" />
                                  </div>
                                )}
                                <div className="p-3">
                                  <h4 className="text-white text-sm font-semibold truncate">{game.name}</h4>
                                  {game.provider && (
                                    <p className="text-gray-500 text-xs truncate">{game.provider}</p>
                                  )}
                                </div>
                                {/* Seçim göstergesi */}
                                <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                                  isAlreadyAdded
                                    ? 'bg-yellow-500/90'
                                    : isSelected
                                    ? 'bg-orange-500'
                                    : 'bg-dark-800/80 border border-dark-600'
                                }`}>
                                  {isAlreadyAdded ? (
                                    <Check className="w-4 h-4 text-white" />
                                  ) : isSelected ? (
                                    <Check className="w-4 h-4 text-white" />
                                  ) : null}
                                </div>
                                {isAlreadyAdded && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-yellow-500/90 text-center py-1">
                                    <span className="text-xs text-white font-medium">Zaten Eklendi</span>
                                  </div>
                                )}
                              </motion.div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  <div className="p-4 border-t border-dark-700 flex items-center justify-between">
                    <button
                      onClick={() => {
                        // Tümünü seç/kaldır
                        if (selectedHomeLobbyGames.size === lobbyGames.filter(g => !games.some(pg => pg.name === g.name)).length) {
                          setSelectedHomeLobbyGames(new Set());
                        } else {
                          const newSet = new Set<string>();
                          lobbyGames.forEach(g => {
                            if (!games.some(pg => pg.name === g.name)) {
                              newSet.add(g.id);
                            }
                          });
                          setSelectedHomeLobbyGames(newSet);
                        }
                      }}
                      className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                    >
                      {selectedHomeLobbyGames.size === lobbyGames.filter(g => !games.some(pg => pg.name === g.name)).length
                        ? 'Tümünü Kaldır'
                        : 'Tümünü Seç'}
                    </button>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setIsHomeLobbySelectModalOpen(false)}
                        className="px-4 py-2.5 bg-dark-700 text-gray-300 rounded-lg hover:bg-dark-600 transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        onClick={() => addGamesToHomePopular(selectedHomeLobbyGames)}
                        disabled={selectedHomeLobbyGames.size === 0}
                        className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>{selectedHomeLobbyGames.size} Oyun Ekle</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Backup Tab */}
          {activeTab === 'backup' && hasAccess(userRole, 'backup') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-6"
            >
              <div className="flex items-center space-x-3 mb-6">
                <Database className="w-8 h-8 text-cyan-400" />
                <div>
                  <h2 className="text-xl font-bold text-white">Veritabanı Yedekleme</h2>
                  <p className="text-sm text-gray-400">Tüm Firestore verilerini JSON formatında indirin</p>
                </div>
              </div>

              <div className="bg-dark-700/50 rounded-xl p-6 border border-dark-600/50">
                <div className="flex flex-col items-center justify-center space-y-6">
                  {/* Backup Info */}
                  <div className="text-center">
                    <FileJson className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Firebase Firestore Yedekleme</h3>
                    <p className="text-gray-400 text-sm max-w-md">
                      Bu işlem tüm veritabanı koleksiyonlarını (kullanıcılar, oyunlar, bannerlar, promosyonlar vb.)
                      JSON formatında indirir. Yedekleme işlemi birkaç dakika sürebilir.
                    </p>
                  </div>

                  {/* Collections List */}
                  <div className="w-full max-w-md bg-dark-800/50 rounded-lg p-4 border border-dark-600/30">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Yedeklenecek Koleksiyonlar:</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                      <span>• users</span>
                      <span>• banners</span>
                      <span>• populerOyunlar</span>
                      <span>• pageGames_slots_*</span>
                      <span>• pageGames_casino_*</span>
                      <span>• promosyonlar</span>
                      <span>• turnuvalar</span>
                      <span>• bonusKodlari</span>
                      <span>• sponsors</span>
                      <span>• marketProducts</span>
                      <span>• messages</span>
                      <span>• applications</span>
                    </div>
                  </div>

                  {/* Progress */}
                  {backupProgress && (
                    <div className={`w-full max-w-md p-4 rounded-lg border ${
                      backupComplete
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-cyan-500/10 border-cyan-500/30'
                    }`}>
                      <div className="flex items-center space-x-3">
                        {backupComplete ? (
                          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                        ) : (
                          <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin flex-shrink-0" />
                        )}
                        <span className={`text-sm ${backupComplete ? 'text-green-400' : 'text-cyan-400'}`}>
                          {backupProgress}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Download Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={downloadBackup}
                    disabled={backupLoading}
                    className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                      backupLoading
                        ? 'bg-dark-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40'
                    }`}
                  >
                    {backupLoading ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Yedekleniyor...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        <span>Yedeklemeyi İndir</span>
                      </>
                    )}
                  </motion.button>

                  {/* Warning */}
                  <div className="flex items-start space-x-2 text-xs text-gray-500 max-w-md">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Yedekleme dosyasını güvenli bir yerde saklayın. Bu dosya hassas kullanıcı verileri içerebilir.
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

        </div>
      </div>
    </>
  );
};

export default AdminPanel;
